import { Building, HeatSource, HeatSourceTotals, Inspection, InspectionStatus, InspectionTerm } from '../types';
import { filenameFromContentDisposition } from '../utils/download';

// JEDYNE źródło base URL API web-admina (patrz KONWENCJE w CLAUDE.md): konfigurowalne
// przez REACT_APP_API_URL (.env/.env.production = '/api' — relative przez nginx proxy).
// Fallback '/api' celowo BEZ wariantów localhost — backend binduje 10.10.10.101, a stare
// lokalne fallbacki (3001/3010) wskazywały donikąd. Eksport ADDYTYWNY (kontrakt-API bez zmian).
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// P4 (#19): przy AUTH_ENFORCE=1 KAŻDE wywołanie /api/* wymaga JWT. AuthContext trzyma
// token w localStorage('authToken') — ten wrapper dokłada go ADDYTYWNIE do wszystkich
// wywołań w tym pliku (wzorzec = mobile authedFetch z #88): bez tokenu zachowuje się jak
// goły fetch (kompatybilność z AUTH_ENFORCE=0), nie nadpisuje jawnego Authorization.
// `X-Client: web-admin` (2026-10-09, kontrakt-API) idzie ZAWSZE, także bez tokenu: backend rozpoznaje
// panel po nagłówku, nie po roli — tablety logują się kontem superadmin. Bramka wersji tabletu
// (MIN_CLIENT_VERSION, backend middleware/clientVersion.js) NIGDY nie blokuje żądań z tym nagłówkiem.
// Eksportowany dla testów.
export async function authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
  let token: string | null = null;
  try { token = localStorage.getItem('authToken'); } catch (_) { /* SSR/test bez localStorage */ }
  const existing = { 'X-Client': 'web-admin', ...((init.headers ?? {}) as Record<string, string>) };
  if (!token) return fetch(input, { ...init, headers: existing });
  const hasAuth = Object.keys(existing).some((k) => k.toLowerCase() === 'authorization');
  const headers = hasAuth ? existing : { ...existing, Authorization: `Bearer ${token}` };
  const res = await fetch(input, { ...init, headers });
  // Auto-logout na 401 (odpowiednik mobile #91): MIELIŚMY token, a backend go odrzucił
  // (wygasły JWT 24h / unieważniony) → czyścimy sesję i przeładowujemy — bramka
  // (ProtectedRoute) pokaże LoginPage. Bez pętli: login idzie GOŁYM fetch w AuthContext
  // (złe hasło nie przechodzi tędy), a po wyczyszczeniu następne wywołania nie mają
  // tokenu → gałąź powyżej (goły fetch) i brak kolejnego reloadu z tego kodu.
  if (res.status === 401) {
    try { localStorage.removeItem('authToken'); localStorage.removeItem('authUser'); } catch (_) { /* no-op */ }
    try { window.location.reload(); } catch (_) { /* test/SSR bez window */ }
  }
  return res;
}

// Odpowiedź 202 z POST /buildings/:id/import-apartments-ceeb?commit=true —
// bot importu został wystartowany jako subprocess (spawn).
export interface CeebImportStartResponse {
  status: string;       // np. 'started'
  jobId: string;        // identyfikator joba subprocesu
  buildingId: string;   // budynek, dla którego ruszył import
  mode: string;         // tryb (np. 'commit')
}

// Odpowiedź GET /buildings/:id/import-status — stan pliku statusu zapisywanego
// przez subprocess importu. `finishedAt` jest KLUCZOWY dla anty-stale pollingu (Z.12):
// odróżnia świeży terminal od starego runu.
export interface CeebImportStatusResponse {
  status: 'idle' | 'running' | 'done' | 'error';
  found?: boolean;          // czy budynek znaleziony w CEEB
  locals?: number;          // liczba zaimportowanych lokali
  heatSources?: number;     // liczba zaimportowanych źródeł ciepła (relacyjnie)
  ceebId?: string | null;   // ceeb_id ustawiony na budynku po imporcie
  finishedAt?: string | null; // ISO timestamp zakończenia (done/error) lub null gdy trwa
  error?: string | null;    // komunikat błędu gdy status === 'error'
}

// Faza 1 (2026-09-02, #243): raport ostatniego PEŁNEGO skanu lustra CEEB (cron 05:15,
// `ceebApiImport.js --report`). Backend: GET /api/ceeb/import-nightly — `status: 'idle'` gdy skan
// z raportem jeszcze nie biegł; `ok:false` + `error` gdy skan padł (np. login). Kształt: ceeb-sync.js.
export interface CeebNightlyImportStatus {
  status: 'idle' | 'done' | 'error';
  ok?: boolean;
  startedAt?: string;
  finishedAt?: string;
  durationMin?: number;
  mode?: string | null;
  protocols?: number;
  processed?: number;
  createdBuildings?: number;
  createdAddresses?: string[];
  newProtocols?: number;
  changedBuildings?: number;
  // pkt 6 (2026-09-14): rekordy kontroli lokali odtworzone z CEEB tej nocy i przeglądy założone (addytywne).
  recordsInserted?: number;
  inspectionsCreated?: number;
  // pkt 14 (2026-09-13): budynki z pustym „Zakresem kontroli" (liczby przewodów) w CEEB; null = nie liczono.
  flueCountsMissing?: number | null;
  // 2026-10-21 (addytywne): przeglądy, których status dogonił CEEB (np. akceptacja ręczna w CEEB — Harcerska 9).
  inspectionStatusSynced?: number;
  // 074 (addytywne): protokoły usunięte w CEEB (pełny skan nie widział ich na liście); null = nie sprawdzano.
  ghostProtocols?: {
    marked: { buildingId: string; address?: string | null; caseNumber: string; status: string | null }[];
    skipped: string | null;
  } | null;
  errorCount?: number;
  errors?: { ceebId?: string; address?: string; error?: string }[];
  error?: string | null;
}

// --- Pipeline (widok budynków wg workflow kominiarza — spec §1a/§4) ---------
// 7 stanów lifecycle (migr. 017 CHECK) — spójne z backend/src/routes/pipelineQuery.js VALID_STATES.
export type LifecycleState =
  | 'NOWY'
  | 'ZAPLANOWANY'
  | 'W_TERENIE'
  | 'DO_POWROTU'
  | 'GOTOWY_DO_CEEB'
  | 'W_CEEB'
  | 'ZAMKNIETY';

// Klucze sortowania listy pipeline (pipelineQuery.js VALID_SORTS). `term` = najbliższy termin;
// `first_term`/`second_term` (2026-10-09, addytywne) = kolumny „Termin I”/„Termin II”.
export type PipelineSort = 'ceeb_activity' | 'readiness' | 'term' | 'address' | 'ceeb_pending' | 'ceeb_term'
  | 'first_term' | 'second_term';
/** 2026-09-14: kierunek sortowania listy; brak = domyślny kierunek danego sortu (backend DEFAULT_DIR). */
export type PipelineSortDir = 'asc' | 'desc';

// Jeden wiersz listy pipeline (backend mapPipelineRow). DWA NIEZALEŻNE WYMIARY (§1a):
//  - field (postęp terenowy): apt_total / apt_terminal (zrobione) / apt_open (do powrotu),
//    next_term_date, lifecycle_state — STERUJE stanem lifecycle;
//  - ceeb (status wysyłki): apt_in_ceeb (terminalne już w CEEB) vs apt_pending_ceeb
//    (terminalne czekające na „Wyślij do CEEB") — OSOBNY wymiar, NIE bramkuje na GOTOWY_DO_CEEB.
export interface PipelineBuilding {
  id: string;
  address: string;              // złożony „ulica nr, miasto" (backend)
  street: string | null;
  house_number: string | null;
  city: string | null;
  postal_code: string | null;
  special_marking: string | null;
  ceeb_id: string | null;
  lifecycle_state: LifecycleState;
  // wymiar field (teren)
  apt_total: number;
  apt_terminal: number;
  apt_open: number;
  next_term_date: string | null; // 'YYYY-MM-DD' (to_char) | null
  // 2026-09-13 (pkt 20/21/23, addytywne): termin następnej kontroli wg CEEB, terminy I/II planu,
  // data rozpoczęcia prac (pierwszy zapis lokalu z terenu w tym przeglądzie).
  next_inspection_date?: string | null;
  ceeb_last_activity?: string | null; // 051 (pkt 13): ostatnia aktywność budynku w CEEB, 'YYYY-MM-DD' (addytywne)
  first_term_date?: string | null;
  second_term_date?: string | null;
  // pkt 14 (2026-09-13): liczby przewodów z CEEB puste/zero w najnowszym przeglądzie → czerwony wykrzyknik.
  flue_counts_missing?: boolean;
  // feat/bulk-reschedule-unplan: I termin minął i nikt nie zapisał lokalu → czerwona data, chip „Po terminie”.
  overdue?: boolean;
  // Migr. 073 (właściciel 21.10, addytywne): „W kolejce” — przegląd bez terminów wypuszczony na tablet; I termin ustawi
  // pierwszy zapis lokalu w terenie. `queued` = w kolejce TERAZ (ta sama reguła co filtr listy), `queuedAt` = kiedy wstawiono.
  queued?: boolean;
  queuedAt?: string | null;
  started_at?: string | null;
  // wymiar ceeb (wysyłka per-lokal)
  apt_in_ceeb: number;
  apt_pending_ceeb: number;
  inspectionId: string | null;   // najnowszy przegląd budynku (do CEEB push / [Otwórz])
  // Kominiarz przypisany do najnowszego przeglądu (kolumna „Przypisany", #11).
  // OPCJONALNE/addytywne: backend pipelineQuery jeszcze tego nie zwraca — do wpięcia
  // po stronie Strumienia A (i.assigned_to w SELECT + mapPipelineRow). Do tego czasu
  // kolumna degraduje się do „—".
  assigned_to?: string | null;
  // 036: cykl akceptacji CEEB (zielony przycisk superadmina).
  ceebStatus?: string | null;          // 'Wersja robocza' | 'Wprowadzony' | 'Zaakceptowany'
  ceebCaseNumber?: string | null;      // numer sprawy w CEEB (NNNNNN/RRRR)
  ceebSubmittedAt?: string | null;     // bot zapisał protokół „na czysto"
  ceebAcceptedAt?: string | null;      // Data zaakceptowania
  // 2026-10-21 (addytywne): status TEGO numeru sprawy w lustrze CEEB (nocny import). Gdy „Zaakceptowany" (akceptacja
  // ręczna w CEEB), backend podnosi też `ceebStatus` do „Zaakceptowany" — bez zielonego „Akceptuj".
  ceebMirrorStatus?: string | null;
  // 074 (addytywne): protokół usunięty w CEEB (pełny nocny skan go nie widzi) — znacznik wiersza; null = brak.
  ceebDeletedProtocol?: { caseNumber: string; detectedAt: string | null } | null;
  // #365 (addytywne): aplikacja wysyłała protokół tego przeglądu (audyt CEEB_PUSH_*). Tylko wtedy „Finalizuj w CEEB"
  // wymaga potwierdzonych „Uwag", a obok stoi niebieski „Wyślij do CEEB" (zapis roboczy, który je potwierdza).
  ceebAppPushed?: boolean;
  // #404 (addytywne, D6): wstrzymany drugi protokół w roku obok zaakceptowanego — „Finalizuj w CEEB" dozwolony także
  // przy etapie ZAMKNIETY (bramka serwera: ceebPushQuery.SECOND_PROTOCOL_RELEASABLE_SQL).
  secondProtocolReleasable?: boolean;
  // #404 (addytywne): protokoły tego roku spoza wersji roboczej z najnowszego śladu reguły drugiego protokołu
  // (`[]` = ślad „niepotwierdzone", null = śladu brak) — okno „Finalizuj w CEEB" wymaga wtedy świadomego potwierdzenia.
  secondProtocolIssued?: Array<{ caseNumber: string; status: string | null; year?: number | string | null }> | null;
  // 2026-10-21 (addytywne, kontrakt-API): „Wyślij do CEEB z nieobecnościami" (Prusa 28). `inaccessiblePendingCeeb` = numery
  // lokali z nieobecnością / odmową lokatora w najnowszym przeglądzie, jeszcze poza CEEB (to je wyśle przycisk);
  // `inaccessibleSentence` = lokale, które bot wpisze do zdania „…nie udostępniono lokali nr.:…" (reguła runnera bota).
  // Stary backend — pola brak (przycisk się nie pokazuje).
  inaccessiblePendingCeeb?: string[];
  inaccessibleSentence?: string[];
  // Numer, który dostanie KOLEJNY dodany termin — etykieta „Zaplanuj N termin" (2026-07-28).
  // Wiersz listy nie niesie dat terminów, więc numeru nie da się policzyć na froncie;
  // liczy go backend (mapPipelineRow → nextFreeTermNumber). null = budynek bez przeglądu.
  nextTermNumber?: number | null;
  // 055 (właściciel 2026-10-02, addytywne): zarządca budynku — kolumna „Zarządca" (skrót, pełna
  // nazwa jako fallback/tooltip) i akcja zbiorcza „Przypisz zarządcę".
  clientId?: string | null;
  clientName?: string | null;
  clientShortName?: string | null;
}

// Odpowiedź obu endpointów dodatkowych terminów. `additionalTerms` to SUROWA tablica JSONB
// (indeks = argument DELETE), `terms` to ponumerowana lista WSZYSTKICH terminów z backendu.
export interface AdditionalTermsResponse {
  id: string;
  additionalTerms: Array<{ date: string; addedAt?: string; reason?: string }>;
  terms: InspectionTerm[];
  // Numer, który dostanie NASTĘPNY termin (pierwszy wolny slot) — po dodaniu/usunięciu
  // przesuwa się, więc etykieta przycisku musi iść stąd, nie z długości `terms`.
  nextTermNumber?: number | null;
}

// Parametry GET /api/buildings/pipeline. stage (klik w chip) wygrywa z group;
// bez stage: group=active (domyślnie Zaplanowany..Gotowy) | all.
export interface PipelineParams {
  stage?: LifecycleState;
  group?: 'active' | 'all';
  search?: string;
  sort?: PipelineSort;
  dir?: PipelineSortDir;
  page?: number;
  pageSize?: number;
  /** E2: filtr po zarządcy (`clients.id`); pusty/pominięty = wszyscy. */
  clientId?: string;
  /** 068 (C3): filtr po kategorii zarządcy — id kategorii albo 'none' (bez zarządcy / bez kategorii). */
  clientCategoryId?: string;
  /** feat/bulk-reschedule-unplan: tylko zaległe — I termin minął i 0 zapisów lokali (chip „Po terminie”). */
  overdue?: boolean;
  /** Migr. 073: tylko kolejka (chip „W kolejce”). Bez tego etapy i „Aktywne” kolejki nie zwracają. */
  queued?: boolean;
  /** 074: tylko budynki ze znacznikiem „Protokół … usunięty w CEEB” (chip „Usunięte w CEEB”). */
  ceebDeleted?: boolean;
  /** B1 (Bronek 21.10): tylko budynki jednorodzinne (`type = 'jednolokalowy'`, chip „Jednorodzinne”). */
  singleFamily?: boolean;
}

// Koperta odpowiedzi GET /api/buildings/pipeline (server-side paginacja).
export interface DuplicateCandidate {
  id: string;
  address: string;
  street: string;
  house_number: string;
  city: string;
  postal_code?: string | null;
  ceeb_id: string | null;
  lifecycle_state: LifecycleState;
  similarity: number;
  /** #426: tylko w `exact` — 'exact' = ten sam klucz adresu, 'words' = ulica skrócona w jedną
   *  lub drugą stronę („Niepokólczyckiego 48" ↔ „płk. Franciszka Niepokólczyckiego 48"). Oba blokują
   *  domyślnie; dodanie mimo to tylko po świadomym potwierdzeniu (create z `confirmDuplicate`). */
  match?: 'exact' | 'words';
}
/** #426: istniejący budynek z 409 BUILDING_DUPLICATE_ADDRESS (POST /buildings). */
export interface ExistingBuildingRef {
  id: string;
  address: string;
  ceeb_id: string | null;
  match?: 'exact' | 'words';
}
/** Błąd POST /buildings — przy 409 code:'BUILDING_DUPLICATE_ADDRESS' niesie `existing`. */
export interface BuildingCreateError extends Error {
  status?: number;
  code?: string;
  existing?: ExistingBuildingRef[];
}
export interface DuplicateCheckResponse {
  exact: DuplicateCandidate[];
  similar: DuplicateCandidate[];
}

export interface PipelineResponse {
  data: PipelineBuilding[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  stage: LifecycleState | null;      // null gdy filtr po grupie, nie po chipie
  group: 'active' | 'all' | null;    // null gdy wybrano konkretny stage
  sort: PipelineSort;
  dir?: PipelineSortDir; // addytywne (2026-09-14)
}

// Odpowiedź GET /api/buildings/pipeline/counts — liczniki do chipów (§3) + pending_ceeb
// (liczba budynków z terminalnym lokalem POZA CEEB, drugi wymiar §1a). counts: zawsze 7 stanów.
export interface PipelineCountsResponse {
  counts: Record<LifecycleState, number>;
  total: number;
  pending_ceeb: number;
  /** feat/bulk-reschedule-unplan: liczba zaległych (I termin minął, 0 zapisów) w tym samym zakresie search/clientId. */
  overdue?: number;
  /** Migr. 073 (addytywne): liczba budynków w kolejce (chip „W kolejce (N)”). „Odłożone” (A5) usunięte — kontrakt #470. */
  queued?: number;
  /** Migr. 073 (addytywne): kolejka per stan — odejmowana od chipów etapów i „Aktywne” (te listy kolejki nie pokazują). */
  queued_counts?: Partial<Record<LifecycleState, number>>;
  /** 074 (addytywne): budynki ze znacznikiem „Protokół … usunięty w CEEB” (chip „Usunięte w CEEB (N)”). */
  ceeb_deleted?: number;
  /** B1 (addytywne): budynki jednorodzinne (`type = 'jednolokalowy'`) w tym samym zakresie search/zarządca (chip „Jednorodzinne (N)”). */
  single_family?: number;
}

/** Migr. 073: wynik POST /api/inspections/queue i /queue/remove — per budynek (każdy we własnej transakcji). */
export interface InspectionQueueItem {
  buildingId: string;
  inspectionId: string;
  lifecycleState?: LifecycleState | null;
}
export interface InspectionQueueSkipped {
  buildingId: string;
  /** NOT_FOUND | ALREADY_QUEUED | HAS_OPEN_INSPECTION | NOT_QUEUEABLE | QUEUE_NOT_APPLICABLE | NOT_QUEUED |
   *  INSPECTION_HAS_VISITS | INSPECTION_HAS_WORK | INSPECTION_IN_CEEB | ERROR */
  code: string;
  reason: string;
}
export interface InspectionQueueResponse {
  queued?: InspectionQueueItem[];
  removed?: InspectionQueueItem[];
  skipped: InspectionQueueSkipped[];
}

// Odpowiedź 202 z POST /api/buildings/:id/ceeb-push — bot fillCeebDraft.js (domyślnie z finalizacją; `draft: true` = wersja robocza)
// wystartowany dla TERMINALNYCH-NIESYNCHRONIZOWANYCH lokali najnowszego przeglądu (§1a).
export interface CeebPushStartResponse {
  status: string;              // 'started'
  jobId: string;
  buildingId: string;
  inspectionId: string | null;
  apartmentCount: number;      // ile lokali poszło do bota
  // 2026-10-21 (addytywne): 'draft-inaccessible' przy wysyłce z nieobecnościami (zawsze wersja robocza).
  mode?: string;
}

// Odpowiedź GET /api/buildings/:id/ceeb-push-status — poll status-file pisanego przez
// BACKEND (nie bota). idle (brak pliku) | running | done | error. Pola opcjonalne zależą od fazy.
/** Wynik JEDNEGO budynku w partii (2026-09-01). */
export interface CeebBatchWynik {
  buildingId: string;
  adres: string;
  /** `ok` = wysłane, `pominiety` = 409 (brak lokali / push w toku), `blad` = awaria. */
  status: 'ok' | 'pominiety' | 'blad';
  synced?: number;
  apartmentCount?: number;
  saved?: boolean;
  error?: string | null;
  ostrzezeniaDanych?: Array<{ pole: string; problem: string; wartosc: string }>;
  /** Komunikat dla operatora — np. drugi protokół w tym roku zapisany tylko jako wersja robocza (2026-10-10). */
  notice?: string | null;
  /** Kod odmowy pominiętego budynku (np. EDIT_ISSUED_ALARM_HOLD, PROTOCOL_ISSUED). */
  code?: string | null;
}

/** Status partii — `GET /buildings/ceeb-push-batch/:jobId`. */
export interface CeebPushBatchStatus {
  status: 'idle' | 'running' | 'done' | 'error';
  jobId?: string;
  total?: number;
  done?: number;
  current?: { buildingId: string; adres: string } | null;
  wyniki?: CeebBatchWynik[];
  startedAt?: string;
  finishedAt?: string;
  error?: string;
}

/**
 * Postęp trwającej wysyłki (D1, 2026-10-22, kontrakt-API, addytywne) — `progress` w `ceeb-push-status` i `push-running`
 * przy `status: 'running'`. `text` gotowe do UI („Lokal 5 z 13 · 6 min", „Faza A: zakładanie protokołu · 1 min",
 * „Ponawiam (CEEB nie załadował listy)…"). Backend: utils/ceebPushProgress.describePushProgress.
 */
export interface CeebPushProgress {
  stage: string;
  label: string;
  text: string;
  current: number | null;
  total: number | null;
  item: string | null;
  elapsedMin: number | null;
  startedAt: string | null;
  updatedAt: string | null;
}

/** Automatyczne ponowienie po stanie przejściowym CEEB (D3, addytywne). */
export interface CeebPushRetryInfo {
  attempt: number;
  code: string | null;
  label: string | null;
  reason: string | null;
  at?: string;
  waiting?: boolean;
  retryAt?: string;
}

/** `GET /api/ceeb/push-running` (G12) — budynki z trwającą wysyłką/poprawką w CEEB. */
export interface CeebPushRunningResponse {
  running: Array<{
    buildingId: string; jobId: string | null; startedAt: string | null; mode: string | null;
    /** D1 (addytywne): postęp — brak u starszego backendu. */
    progress?: CeebPushProgress | null;
  }>;
}

/** `GET /api/ceeb/push-report?since=` (D4, 2026-10-22) — raport wysyłek / finalizacji / akceptacji z okna (domyślnie 24 h). */
export interface CeebPushReportItem {
  kind: 'push' | 'finalize' | 'accept' | 'phase-a';
  kindLabel: string;
  jobId: string;
  buildingId: string | null;
  address: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMin: number | null;
  result: 'ok' | 'partial' | 'error' | 'running' | 'unknown';
  resultLabel: string;
  reason: string;
  mode: string | null;
  caseNumber: string | null;
  apartmentCount: number | null;
  synced: number | null;
  retried: { code: string | null; label: string | null; reason: string } | null;
  userName: string | null;
}

export interface CeebPushReport {
  since: string;
  until: string;
  generatedAt: string;
  total: number;
  counts: { ok: number; partial: number; error: number; running: number; unknown: number };
  retried: number;
  items: CeebPushReportItem[];
}

export interface CeebPushStatusResponse {
  status: 'idle' | 'running' | 'done' | 'error';
  jobId?: string;
  buildingId?: string;
  inspectionId?: string | null;
  apartmentCount?: number;
  synced?: number;             // ile AIR oznaczono jako zsynchronizowane (done)
  startedAt?: string;          // running
  finishedAt?: string | null;  // done/error
  exitCode?: number | null;
  signal?: string | null;
  error?: string;              // gdy status === 'error'
  // Zadanie 3.4 (2026-08-19): panel pokazuje TREŚĆ awarii i wynik PER LOKAL, nie tylko licznik.
  /** Powód oznaczenia/nieoznaczenia lokali (np. „zapisano; oznaczono 2/6 lokali"). */
  markReason?: string;
  /** Czy bot faktycznie zapisał wersję roboczą (meta.saved) — „Wysłano (0)" bez tego to porażka. */
  saved?: boolean;
  /**
   * Wynik per lokal: czy oznaczony w CEEB i dlaczego (Zadanie 3.1). `code` przy głośnych powodach, np.
   * 'ceeb_copy_blocked' (wiersz lokalu w CEEB już wystawiony — panel może zaproponować poprawkę protokołu).
   */
  perApartment?: Array<{ nr: string; airId?: string; synced: boolean; reason: string; code?: string }>;
  /** Błędy kroków bota (meta.errors) — dotąd niewidoczne w panelu. */
  errors?: Array<{ step: string; error: string }>;
  /** Podsumowanie weryfikacji odczytem (meta.verifySummary, Zadanie 1). */
  verifySummary?: { total: number; ok: number; failed: number; info: number; shots: number } | null;
  /** Załącznik: nazwa pliku i czy był już w CEEB (Zadanie 3.6). */
  // 079 (kontrakt-API, addytywnie): `kind` = 'user_cards' gdy bot wgrywał własny plik operatora (karty mieszkań z panelu).
  attachment?: { uploaded: string | null; alreadyPresent: boolean; kind?: string | null } | null;
  /** Push przekroczył twardy limit czasu i został zatrzymany (Zadanie 3.5). */
  timedOut?: boolean;
  /** D1 (2026-10-22, addytywne): postęp przy `running` (etap, lokal X z N, czas). */
  progress?: CeebPushProgress | null;
  /** D3 (addytywne): trwa przerwa przed automatycznym ponowieniem / druga próba (`running`). */
  retry?: CeebPushRetryInfo | null;
  /** D3 (addytywne): wynik po automatycznym ponowieniu (terminal). */
  retried?: CeebPushRetryInfo | null;
  /** Status `running` osierocony (restart backendu / brak wyniku w limicie) — traktowany jak błąd. */
  stale?: boolean;
  /**
   * Sprzeczne dane budynku wykryte PRZED wysyłką (2026-08-31, `utils/buildingDataChecks.js`).
   * Nie blokują wysyłki — to my wpisujemy te wartości do rejestru państwowego, więc operator
   * musi je zobaczyć. Backend liczył je i wysyłał na Telegram, ale panel ich NIE POKAZYWAŁ.
   */
  ostrzezeniaDanych?: Array<{ pole: string; problem: string; wartosc: string }>;
  /**
   * Potwierdzenie statusu protokołu W REJESTRZE po finalizacji (2026-09-01).
   * `stamp:false` znaczy „bot kliknął Zapisz, ale rejestr tego nie potwierdza" — wtedy
   * `ceeb_status` NIE jest stemplowany i zielony „Akceptuj" się nie zapala.
   */
  statusConfirm?: { stamp: boolean; status: string | null; reason: string | null } | null;
  /** Lokale, których wiersz w CEEB to już wystawiona kopia z poprzedniego cyklu (reguła 3a ceebPushQuery). */
  copyBlocked?: { nrs: string[]; message: string } | null;
  /**
   * Lokale, którym bot zaznaczył „wspólne źródło" z reguły domyślnej (Bronek 2026-10-14, kontrakt-API #419, addytywne):
   * zapis bez informacji o źródłach w budynku ze wspólnym C.O. — informacja do sprawdzenia przed finalizacją.
   */
  defaultConnected?: { nrs: string[]; message: string } | null;
  // --- Poprawka wystawionego protokołu (2026-10-09, kontrakt-API „edit-issued", addytywne) ---
  /**
   * Tryb przebiegu (ten sam status-file dla wszystkich): `push` | `draft-recheck` | `finalize-only` | `edit-issued` |
   * `draft-second-protocol` | `draft-phase-a-unverified` | `draft-inaccessible` | `draft-renumbered` (2026-10-21: numery lokali
   * zmieniono w tym roku — w CEEB mogą być kopie pod starymi numerami, więc tylko wersja robocza + `notice`).
   * `draft-second-protocol` / `draft-phase-a-unverified` (2026-10-10, D6): pod adresem jest już protokół
   * z tego roku spoza wersji roboczej (albo decyzji Fazy A nie dało się potwierdzić) — bot zapisał protokół WYŁĄCZNIE
   * jako wersję roboczą.
   */
  mode?: string;
  /** Komunikat dla operatora do pokazania przy wyniku wysyłki (także przy błędzie) — patrz `mode`. */
  notice?: string | null;
  /** Szczegóły reguły drugiego protokołu: numer tej wysyłki + protokoły tego roku już w CEEB. */
  secondProtocol?: {
    mode: string;
    caseNumber: string;
    issued: Array<{ caseNumber: string; status: string | null; year: number | string | null }>;
  } | null;
  /** Tryb edit-issued: próba bez zapisu — nic nie zmieniono w CEEB. */
  dryRun?: boolean;
  /** Bieżący krok poprawki („Kopia zapasowa protokołu z CEEB" | „Bot poprawia protokół" | „Kopia po zmianie i porównanie"). */
  step?: string | null;
  /** Kopia protokołu z CEEB przed / po poprawce (PDF: `ceebEditIssuedApi.snapshotPdf`). */
  snapshotBeforeId?: string | null;
  snapshotAfterId?: string | null;
  /**
   * Kod wyniku. Poprawka: BACKUP_FAILED · AFTER_SNAPSHOT_FAILED (kopii po zmianie nie dało się zrobić/porównać —
   * nic nie oznaczono, sprawdź w CEEB ręcznie) · EDIT_ISSUED_ALARM · EDIT_ISSUED_NOOP (`status: 'done'`, ale w CEEB nic
   * się nie zmieniło — wszystkie lokale wstrzymane, protokół zamknięty bez zapisu). Odmowa bota w zwykłej wysyłce:
   * TARGET_STATUS_REJECTED (gotowy komunikat) · pozostałe TARGET_* z diagnostyką bota w `error`/`errors`.
   * Alarm poprawki wstrzymuje każdy zapis bota: EDIT_ISSUED_ALARM_HOLD (409 przy starcie). `error` = gotowe polskie zdanie.
   */
  code?: string | null;
  /** 'edit-issued' = UI może zaproponować „Popraw wystawiony protokół w CEEB" (superadmin, flaga włączona). */
  next?: string | null;
  /** Status protokołu odczytany z CEEB przy odmowie bota (TARGET_STATUS_REJECTED). */
  ceebStatus?: string | null;
  caseNumber?: string | null;
  editIssued?: CeebEditIssuedResult | null;
  /** Alarm poprawki: coś zmieniło się poza zakresem bota albo protokół spadł do wersji roboczej. */
  alarm?: CeebEditIssuedAlarm | null;
  /** Raport przebiegu bota (`/raporty/<jobId>/`, ten sam origin co panel). */
  reportUrl?: string | null;
}

/** Wynik poprawki wystawionego protokołu w status-file'u (`editIssued`). */
export interface CeebEditIssuedResult {
  protocolAction?: 'zapisz' | 'cancel' | null;
  sentLokale?: string[];
  heldLokale?: Array<{ nr: string; reason: string }>;
  statusAfter?: string | null;
  /** Porównanie kopii przed/po — LICZBY różnic (szczegóły w raporcie przebiegu) + gotowe polskie podsumowanie. */
  diff?: { expected: number; unexpected: number; sideEffects: number; idChanges: number; summary: string } | null;
}

/** Jedna nieoczekiwana zmiana z porównania kopii przed/po (ścieżka pola, wartości, powód alarmu). */
export interface CeebEditIssuedAlarmChange {
  path: string;
  before: unknown;
  after: unknown;
  why: string;
}

/** Alarm poprawki — dalsze poprawki są wstrzymane, dopóki człowiek go nie zdejmie (EDIT_ISSUED_ALARM_HOLD). */
export interface CeebEditIssuedAlarm {
  message: string;
  unexpected: CeebEditIssuedAlarmChange[];
}

/**
 * Błąd tras CEEB z kodem z ciała 4xx (kontrakt-API 2026-10-09): `code` (np. PROTOCOL_ISSUED,
 * CASE_CONFIRM_MISMATCH) i `next` ('edit-issued' = można zaproponować poprawkę wystawionego protokołu).
 */
export interface CeebApiError extends Error {
  status?: number;
  code?: string;
  next?: string | null;
}

async function ceebApiError(response: Response, fallback: string): Promise<CeebApiError> {
  const body = await response.json().catch(() => ({} as any));
  const e = new Error((body as any).error || fallback) as CeebApiError;
  e.status = response.status;
  if ((body as any).code) e.code = (body as any).code;
  if ((body as any).next) e.next = (body as any).next;
  return e;
}

// --- Rejestracja budynku w CEEB (#53 / B9) -----------------------------------
// Odpowiedź 202 z POST /api/buildings/:id/register-ceeb — backend spawnuje bota
// rejestracji (registerCeebBuilding.js) i zwraca kopertę startu (analogicznie do importu).
export interface RegisterCeebStartResponse {
  status: string;       // 'started'
  jobId: string;
  buildingId: string;
  mode: string;         // 'commit'
}

// Konflikt duplikatu (kształt z backend/src/scripts/ceebApiImport.js): ceeb_id zwrócony
// przez CEEB jest już przypisany do INNEGO budynku w naszej bazie — rejestracji nie da się
// bezpiecznie dopiąć. Sygnalizowane polem `ceebIdConflict` w statusie (#53 STANY).
export interface CeebIdConflict {
  takenByBuildingId: string | null;
  takenByAddress: string | null;
}

// Odpowiedź GET /api/buildings/:id/register-ceeb-status — koperta statusu pisana przez
// backend (running + terminal-fallback done/error) i wzbogacana ADDYTYWNIE przez skrypt
// bota (registered/ceebId/ceebIdConflict), dokładnie jak import-status. `finishedAt`
// bramkuje anty-stale polling (odróżnia świeży terminal od starego runu).
export interface RegisterCeebStatusResponse {
  status: 'idle' | 'running' | 'done' | 'error';
  buildingId?: string;
  jobId?: string;
  mode?: string;
  address?: string;
  type?: string | null;
  businessActivity?: string | null;
  startedAt?: string;
  finishedAt?: string | null;      // ISO (done/error) lub null gdy trwa — anty-stale
  exitCode?: number | null;
  error?: string | null;           // komunikat błędu gdy status === 'error'
  // Pola dopisywane przez skrypt bota (mogą nie istnieć w terminalu-fallbacku backendu):
  registered?: boolean;                    // budynek zarejestrowany w CEEB
  ceebId?: string | null;                  // ceeb_id nadany/zaciągnięty po rejestracji
  ceebIdConflict?: CeebIdConflict | null;  // kolizja: ceeb_id zajęty przez inny budynek
}

/**
 * Zadanie 5.6 (2026-08-20): status FAZY A — zadania w tle, które przy dodaniu budynku
 * szuka-lub-zakłada nieruchomość i protokół w CEEB, wysyła inwentaryzację budynku
 * (to CEEB kopiuje lokale z poprzedniej kontroli), importuje prefill i zapisuje numer sprawy.
 *
 * W przeciwieństwie do rejestracji status niesie STAN KAŻDEGO Z PIĘCIU KROKÓW — pasek postępu
 * pokazuje go NA BIEŻĄCO, a po błędzie widać, który krok padł i dlaczego („ponów").
 */
export type PhaseAStepState = 'pending' | 'running' | 'done' | 'skipped' | 'error';

export interface PhaseAStep {
  name: string;
  state: PhaseAStepState;
  info?: string;
}

export interface PhaseAStatusResponse {
  status: 'idle' | 'running' | 'done' | 'error';
  buildingId?: string;
  jobId?: string | null;
  address?: string;
  startedAt?: string;
  finishedAt?: string | null;   // ISO w terminalu, null gdy trwa — anty-stale
  step?: string | null;         // nazwa kroku aktualnie wykonywanego
  stepIndex?: number;           // 1..stepCount
  stepCount?: number;
  steps?: PhaseAStep[];
  caseNumber?: string | null;   // wynik Fazy A — cel wszystkich kolejnych operacji
  decision?: {
    action: 'reuse' | 'create' | 'blocked';
    why?: string;
    blockedBy?: string[] | null;
    /** Protokoły tego roku spoza wersji roboczej pod adresem (2026-10-10, D6 — drugi protokół w roku). */
    issuedThisYear?: Array<{ caseNumber: string; status: string | null; year?: number | string | null }>;
  } | null;
  /** Ostrzeżenie przy `done` (2026-10-10): `SECOND_PROTOCOL_IN_YEAR` = drugi protokół w roku, wysyłka tylko jako wersja robocza. */
  code?: string | null;
  exitCode?: number | null;
  error?: string | null;
}

/**
 * E3 (2026-08-04): paczka podsumowań wybierana FILTRAMI zamiast zaznaczeniem.
 * `dateFrom`/`dateTo` odnoszą się do TERMINÓW przeglądu; `protocolYear` wybiera budynki,
 * które mają protokół CEEB z tego roku (treść PDF-a jest zawsze bieżąca).
 */
export interface SummariesZipFilters {
  clientId?: string;
  dateFrom?: string;
  dateTo?: string;
  protocolYear?: number;
  /** E6: 'jednolokalowy' | 'wielolokalowy'; pominięte = oba typy. */
  buildingType?: string;
  /** E6: co ma trafić do paczki. Pominięte = ['findings'] (zachowanie sprzed E6). */
  // pkt 16 (2026-09-13): `protocols` = protokoły lokali (ten sam PDF co ikona schowka); `cards` usunięte.
  include?: Array<'findings' | 'protocols'>;
}

// F-min (§12–14, 2026-08-04): wiersz historii protokołów CEEB budynku (building_protocols;
// statusy i uuidy wypełnia pełny skan importu, migr. 046). hasPdf = uuid obecny → protokół
// da się pobrać z CEEB (jasper, blok E5).
export interface BuildingProtocolEntry {
  ceebId: string;
  year: number | null;
  ceebStatus: string | null;
  hasPdf: boolean;
  isCurrent: boolean;
  // 074 (addytywne): kiedy nocny skan wykrył, że protokołu nie ma już w CEEB (wiersz zostaje jako historia);
  // `deletedMarker` = ten protokół daje budynkowi znacznik „usunięty w CEEB” (ta sama reguła co lista budynków).
  deletedInCeebAt?: string | null;
  deletedMarker?: boolean;
}

// 078: wybrany protokół CEEB — źródło danych przeglądu (zakres, lokale, źródła ciepła z importu).
export interface PrefillSourceResult {
  prefillSource?: { caseNumber: string; year: number | null; status: string | null } | null;
  /** Rok bez protokołu w lustrze (planowanie zbiorcze) → dane z protokołu bieżącego. */
  prefillSourceNote?: string | null;
}

// E5 (§8, 2026-08-04): hurtowe pobieranie OFICJALNYCH PDF-ów protokołów z CEEB.
// Wzorzec jobowy (spawn + status-file): start → polling → download ZIP.
export interface ProtocolPdfsStatus {
  status: 'running' | 'done' | 'error';
  requested?: number;
  allowed?: number;
  total?: number;
  fetched?: number;
  // Ile z `allowed` budynków ma w CEEB choć jeden protokół z uuid, a ile NIE MA ŻADNEGO.
  // `noUuidCount` liczy wyłącznie WIERSZE historii bez uuid i milczy o budynkach bez
  // historii — przez to „Pobierz protokoły CEEB (8)" cicho pobierało 3 (2026-08-05).
  buildingsWithProtocol?: number;
  buildingsWithoutProtocol?: number;
  noUuidCount?: number;
  skipped?: Array<{ ceebId: string; error: string }>;
  // Komplet (#416): co dołożono z naszej bazy obok protokołów CEEB + braki (budynek bez
  // przeglądu/kart/protokołu) — te same linie co w pominiete.txt.
  include?: ProtocolPackInclude[];
  // Podsumowanie w komplecie ze „Źródłami ciepła" (Bronek 14.10) — echo opcji z POST.
  heatSources?: boolean;
  summaries?: number;
  cards?: number;
  extrasSkipped?: string[];
  error?: string | null;
  finishedAt?: string | null;
}

/** #416: zawartość kompletu obok protokołów CEEB — podsumowania kontroli, karty lokali z podpisami. */
export type ProtocolPackInclude = 'summaries' | 'cards';

/**
 * Opcje pobrania podsumowania kontroli. `heatSources` (Bronek 14.10, kontrakt-API): sekcja
 * „Źródła ciepła" — backend bez tej opcji wydaje kopię dla zarządcy BEZ źródeł ciepła.
 */
export interface SummaryDownloadOptions {
  appendix?: boolean;
  heatSources?: boolean;
}

/** Parametry query włączonych opcji (`appendix=1`, `heatSources=1`) — pusta lista = zapytanie jak dotąd. */
function summaryOptionParams(opts?: SummaryDownloadOptions): string[] {
  const params: string[] = [];
  if (opts?.appendix) params.push('appendix=1');
  if (opts?.heatSources) params.push('heatSources=1');
  return params;
}

/**
 * #315 (kontrakt-API): plik z API + nazwa z `Content-Disposition` (utils/download). `filename`
 * = null, gdy nagłówka brak albo przeglądarka go ukryła (cross-origin) — wołający daje zapasową.
 */
export interface DownloadedFile {
  blob: Blob;
  filename: string | null;
}

async function toDownloadedFile(response: Response): Promise<DownloadedFile> {
  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(response.headers.get('Content-Disposition')),
  };
}

export const protocolPdfsApi = {
  // E6: `protocolYear` opcjonalny — pominięty/pusty = wszystkie lata z historii.
  // #416: `include` (opcjonalny) = komplet — katalog na budynek z protokołami CEEB, podsumowaniem
  // i kartami lokali z podpisami; pominięty = same protokoły CEEB (jak dotąd).
  // `heatSources` (Bronek 14.10) — podsumowanie w komplecie ze „Źródłami ciepła"; pominięte = bez.
  start: async (
    buildingIds: string[],
    protocolYear?: number,
    include?: ProtocolPackInclude[],
    opts?: { heatSources?: boolean },
  ): Promise<{ jobId: string; requested: number; allowed: number; year: number | null }> => {
    const body: { buildingIds: string[]; protocolYear?: number; include?: ProtocolPackInclude[]; heatSources?: boolean } = { buildingIds };
    if (protocolYear) body.protocolYear = protocolYear;
    if (include && include.length) body.include = include;
    if (opts?.heatSources) body.heatSources = true;
    const response = await authedFetch(`${API_BASE_URL}/buildings/protocol-pdfs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Nie udało się uruchomić pobierania protokołów');
    }
    return response.json();
  },
  status: async (jobId: string): Promise<ProtocolPdfsStatus> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/protocol-pdfs/${jobId}`);
    if (!response.ok) throw new Error('Nie udało się odczytać statusu pobierania');
    return response.json();
  },
  // #315 (kontrakt-API): nazwa pliku z Content-Disposition; null = brak/nieczytelny nagłówek.
  download: async (jobId: string): Promise<DownloadedFile> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/protocol-pdfs/${jobId}/download`);
    if (!response.ok) throw new Error('Paczka protokołów niegotowa');
    return toDownloadedFile(response);
  },
};

// Kontrakt #326 (2026-09-30): POST /buildings/heat-source-totals — zestawienie indywidualnych
// źródeł ciepła dla WIELU budynków (wiersz per budynek + suma). Wybór: zaznaczenie ALBO zarządca.
export type HeatSourceTotalsSelection = { buildingIds: string[] } | { clientId: string };

export interface HeatSourceTotalsBuildingRow {
  buildingId: string;
  address: string;
  street: string | null;
  houseNumber: string | null;
  city: string | null;
  totals: HeatSourceTotals;
}

export interface HeatSourceTotalsBulkResponse {
  buildings: HeatSourceTotalsBuildingRow[];
  totals: HeatSourceTotals;
  skipped: { buildingId: string; reason: string }[];
}

// POST /api/buildings/unchecked-apartments (właściciel 2026-10-02, docx: „przycisk zestawiający
// niesprawdzone jak w tablecie + eksport większej ilości budynków"). Lokal niesprawdzony = bez
// terminalnego wyniku w NAJNOWSZYM przeglądzie budynku. `status`: null = nieodwiedzony,
// 'brak_dostepu' / 'odmowa_lokatora' = otwarte (etykiety z STATUS_LABELS). Kontrakt: issue kontrakt-API.
export interface UncheckedApartment {
  id: string;
  apartmentNumber: string;
  /** E3 (migr. 071, kontrakt-API additive #467): oznaczenie na drzwiach i klatka z panelu — tylko do wyświetlania obok numeru. */
  displayLabel?: string | null;
  staircase?: string | null;
  status: 'brak_dostepu' | 'odmowa_lokatora' | null;
  attemptDates: string[]; // 'YYYY-MM-DD' rosnąco (próby z bieżącego przeglądu)
}

export interface UncheckedApartmentsBuilding {
  buildingId: string;
  address: string;
  street: string | null;
  houseNumber: string | null;
  city: string | null;
  inspectionId: string | null;
  apartmentsTotal: number;
  apartments: UncheckedApartment[];
}

export interface UncheckedApartmentsResponse {
  buildings: UncheckedApartmentsBuilding[];
  skipped: { buildingId: string; reason: string }[];
}

// POST /api/buildings/case-numbers (A22, lista Bronka 2026-10): numery spraw CEEB zaznaczonych
// budynków — z bieżącego roku, a bez niego z ostatniego znanego (`year`, `current`). Pusta lista
// `caseNumbers` = budynek bez żadnego numeru. Tylko odczyt. Kontrakt: issue kontrakt-API.
export interface BuildingCaseNumbers {
  buildingId: string;
  address: string;
  street: string | null;
  houseNumber: string | null;
  city: string | null;
  year: number | null;
  current: boolean;
  caseNumbers: { caseNumber: string; status: string | null }[];
}

export interface CaseNumbersResponse {
  currentYear: number;
  buildings: BuildingCaseNumbers[];
  skipped: { buildingId: string; reason: string }[];
}

export const buildingsApi = {
  caseNumbers: async (buildingIds: string[]): Promise<CaseNumbersResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/case-numbers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buildingIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się pobrać numerów protokołów (HTTP ${response.status})`);
    }
    return response.json();
  },

  uncheckedApartments: async (buildingIds: string[]): Promise<UncheckedApartmentsResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/unchecked-apartments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buildingIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się pobrać niesprawdzonych lokali (HTTP ${response.status})`);
    }
    return response.json();
  },

  // Get single building
  getById: async (id: string): Promise<Building> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch building');
    }
    return response.json();
  },

  // Kontrakt #326: zestawienie źródeł ciepła wielu budynków (zaznaczenie albo zarządca).
  heatSourceTotals: async (selection: HeatSourceTotalsSelection): Promise<HeatSourceTotalsBulkResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/heat-source-totals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(selection),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się policzyć źródeł ciepła (HTTP ${response.status})`);
    }
    return response.json();
  },

  // F-min: historia protokołów CEEB (rok → numer → status), najnowsze pierwsze.
  getProtocols: async (id: string): Promise<BuildingProtocolEntry[]> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/protocols`);
    if (!response.ok) {
      throw new Error('Failed to fetch building protocols');
    }
    return response.json();
  },

  // Create new building
  // #426: `confirmDuplicate` = człowiek świadomie potwierdził INNĄ nieruchomość pod adresem
  // istniejącego budynku (bez tego serwer odpowiada 409 BUILDING_DUPLICATE_ADDRESS).
  create: async (
    building: Omit<Building, 'id' | 'createdAt' | 'updatedAt'>,
    opts: { confirmDuplicate?: boolean } = {},
  ): Promise<Building> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(opts.confirmDuplicate ? { ...building, confirmDuplicate: true } : building),
    });
    if (!response.ok) {
      // #426: 409 BUILDING_DUPLICATE_ADDRESS niesie komunikat i `existing` — modal pokazuje istniejący budynek.
      const err = await response.json().catch(() => ({} as any));
      const e = new Error(err.error || 'Failed to create building') as BuildingCreateError;
      e.status = response.status;
      if (err.code) e.code = err.code;
      if (Array.isArray(err.existing)) e.existing = err.existing;
      throw e;
    }
    return response.json();
  },

  // Update building
  // 058: `heatSources` może nieść samo `{ id }` (= źródło bez zmian) i opcjonalne `heatSourcesBase`
  // ({ id: rev } z odczytu) — wtedy serwer kasuje tylko źródła z bazy, a przy zmianie na innym
  // urządzeniu odpowiada 409 code:'HEAT_SOURCES_CHANGED'. Błąd niesie status/code (jak inspekcje).
  update: async (id: string, building: BuildingUpdatePayload): Promise<Building> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(building),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error(err.error || 'Failed to update building') as InspectionUpdateError;
      e.status = response.status;
      if (err.code) e.code = err.code;
      throw e;
    }
    return response.json();
  },

  // Wystartuj import lokali + źródeł ciepła z CEEB dla istniejącego budynku.
  // Backend: POST /api/buildings/:id/import-apartments-ceeb?commit=true —
  // spawnuje bota importu (ceebApiImport.js) i odpowiada 202 { status, jobId, buildingId, mode }.
  // Sam zapis stanu trafia do pliku statusu odczytywanego przez importStatus().
  importFromCeeb: async (id: string): Promise<CeebImportStartResponse> => {
    const response = await authedFetch(
      `${API_BASE_URL}/buildings/${id}/import-apartments-ceeb?commit=true`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to start CEEB import');
    }
    return response.json();
  },

  // Odpytaj stan importu CEEB (no-store — wynik zmienia się między pollami).
  // Backend: GET /api/buildings/:id/import-status — zwraca aktualny stan z pliku statusu.
  // `finishedAt` służy do anty-stale detekcji świeżego terminala (Z.12).
  importStatus: async (id: string): Promise<CeebImportStatusResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/import-status`, {
      method: 'GET',
      cache: 'no-store',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch CEEB import status');
    }
    return response.json();
  },

  // Faza 1 (#243): baner „Lustro CEEB" w PipelineView — jednorazowy odczyt przy montażu, bez pollingu
  // (skan biegnie raz na dobę). Backend: GET /api/ceeb/import-nightly.
  nightlyImportStatus: async (): Promise<CeebNightlyImportStatus> => {
    const response = await authedFetch(`${API_BASE_URL}/ceeb/import-nightly`, {
      method: 'GET',
      cache: 'no-store',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch CEEB nightly import status');
    }
    return response.json();
  },

  // D4 (2026-10-22): raport wysyłek z ostatniej doby — baner obok „Lustro CEEB" (jednorazowy odczyt przy montażu).
  // Backend: GET /api/ceeb/push-report?since=<ISO> (domyślnie 24 h).
  pushReport: async (since?: string): Promise<CeebPushReport> => {
    const q = since ? `?since=${encodeURIComponent(since)}` : '';
    const response = await authedFetch(`${API_BASE_URL}/ceeb/push-report${q}`, { method: 'GET', cache: 'no-store' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Nie udało się pobrać raportu wysyłek do CEEB');
    }
    return response.json();
  },

  // Server-side widok pipeline (spec §4). Zwraca kopertę { data, page, total, ... }.
  // Backend: GET /api/buildings/pipeline?stage&group&search&sort&page&pageSize.
  // Uwaga: page przekazujemy nawet dla 0 (falsy) — porównanie z null, nie truthiness.
  // Pkt 27 (2026-09-13): kontrola duplikatów odporna na kolejność słów i zapis numeru —
  // `exact` blokuje (jak dotąd), `similar` to podpowiedź „czy chodziło o…” z pg_trgm.
  findDuplicates: async (street: string, houseNumber: string, city: string): Promise<DuplicateCheckResponse> => {
    const qs = new URLSearchParams({ street, houseNumber, city });
    const response = await authedFetch(`${API_BASE_URL}/buildings/duplicates?${qs.toString()}`);
    if (!response.ok) throw new Error(`Failed to check duplicates (HTTP ${response.status})`);
    return response.json();
  },

  pipeline: async (params: PipelineParams = {}): Promise<PipelineResponse> => {
    const qs = new URLSearchParams();
    if (params.stage) qs.append('stage', params.stage);
    if (params.group) qs.append('group', params.group);
    if (params.search) qs.append('search', params.search);
    if (params.sort) qs.append('sort', params.sort);
    if (params.dir) qs.append('dir', params.dir);
    if (params.overdue) qs.append('overdue', '1');
    if (params.queued) qs.append('queued', '1');
    if (params.ceebDeleted) qs.append('ceebDeleted', '1');
    if (params.singleFamily) qs.append('singleFamily', '1');
    if (params.page != null) qs.append('page', String(params.page));
    if (params.pageSize != null) qs.append('pageSize', String(params.pageSize));
    if (params.clientId) qs.append('clientId', params.clientId);
    if (params.clientCategoryId) qs.append('clientCategoryId', params.clientCategoryId);

    const q = qs.toString();
    const response = await authedFetch(`${API_BASE_URL}/buildings/pipeline${q ? `?${q}` : ''}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch buildings pipeline');
    }
    return response.json();
  },

  // Liczniki chipów per lifecycle_state + pending_ceeb (spec §3/§1a).
  // Backend: GET /api/buildings/pipeline/counts (respektuje tylko ?search=).
  // E2: `clientId` zawęża TAKŻE chipy — inaczej chip pokazywałby „ZAPLANOWANY 10",
  // a przefiltrowana lista dwa wiersze.
  pipelineCounts: async (search?: string, clientId?: string, clientCategoryId?: string): Promise<PipelineCountsResponse> => {
    const qs = new URLSearchParams();
    if (search) qs.append('search', search);
    if (clientId) qs.append('clientId', clientId);
    // 068 (C3): kategoria zarządcy zawęża chipy tak samo jak listę.
    if (clientCategoryId) qs.append('clientCategoryId', clientCategoryId);
    const q = qs.toString();
    const response = await authedFetch(`${API_BASE_URL}/buildings/pipeline/counts${q ? `?${q}` : ''}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch pipeline counts');
    }
    return response.json();
  },

  // „Wyślij do CEEB" per budynek — domyślnie z finalizacją (⇒ „Wprowadzony"), PER-LOKAL inkrementalnie (spec §1a).
  // Backend: POST /api/buildings/:id/ceeb-push — spawnuje fillCeebDraft.js dla terminalnych
  // niesynchronizowanych lokali; odpowiada 202 { status:'started', jobId, buildingId,
  // inspectionId, apartmentCount }. 409 gdy brak lokali do wysłania.
  // „Finalizuj w CEEB" (właściciel 2026-09-15): gotowy draft w całości w CEEB — bot tylko przeklikuje
  // łańcuch „Wyślij" + „Zapisz" (⇒ Wprowadzony), bez wypełniania pól. Backend: POST /api/buildings/:id/ceeb-finalize,
  // status przez ceebPushStatus (ten sam status-file). 409: brak numeru sprawy / protokół nie jest wersją roboczą.
  // Oba (finalize i push) rzucają CeebApiError z `code`/`next` z ciała 4xx (np. PROTOCOL_ISSUED, next:'edit-issued').
  // E2 Z4: 409 TABLET_PENDING (tablet trzyma nowszą wersję lokali); `ignoreTabletPending` działa tylko dla superadmina.
  finalizeInCeeb: async (buildingId: string, opts: { ignoreTabletPending?: boolean } = {}): Promise<CeebPushStartResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-finalize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts.ignoreTabletPending ? { ignoreTabletPending: true } : {}),
    });
    if (!response.ok) throw await ceebApiError(response, 'Nie udało się uruchomić finalizacji w CEEB');
    return response.json();
  },

  // 2026-10-21 (kontrakt-API): `includeInaccessible` = „Wyślij do CEEB z nieobecnościami" — lokale z nieobecnością / odmową
  // lokatora idą wyłącznie zdaniem „nie udostępniono lokali nr.:…", serwer ZAWSZE zapisuje wersję roboczą (bez finalizacji)
  // i nie przyjmuje obejścia bramki tabletu. 409: NOT_ELIGIBLE_STAGE / APARTMENTS_WITHOUT_RESULT / TERMINAL_PENDING /
  // NO_INACCESSIBLE_PENDING (+ wszystkie dotychczasowe: PROTOCOL_ISSUED, TABLET_PENDING, PUSH_BUSY…).
  pushToCeeb: async (
    buildingId: string,
    opts: { ignoreTabletPending?: boolean; includeInaccessible?: boolean } = {},
  ): Promise<CeebPushStartResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-push`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(opts.includeInaccessible ? { includeInaccessible: true }
        : (opts.ignoreTabletPending ? { ignoreTabletPending: true } : {})),
    });
    if (!response.ok) throw await ceebApiError(response, 'Failed to start CEEB push');
    return response.json();
  },

  // Poll statusu wysyłki CEEB (no-store — zmienia się między pollami).
  // Backend: GET /api/buildings/:id/ceeb-push-status — idle|running|done|error.
  // Backend: POST /buildings/ceeb-push-batch — wysyłka ZBIORCZA. Tylko wersje robocze
  // i sekwencyjnie: wszystkie boty dzielą jedną sesję CEEB (stąd PUSH_CONCURRENCY=1).
  pushToCeebBatch: async (buildingIds: string[]): Promise<{ status: string; jobId: string; total: number }> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/ceeb-push-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buildingIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Nie udało się wystartować wysyłki zbiorczej');
    }
    return response.json();
  },

  ceebPushBatchStatus: async (jobId: string): Promise<CeebPushBatchStatus> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/ceeb-push-batch/${jobId}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Nie udało się pobrać statusu partii');
    return response.json();
  },

  ceebPushStatus: async (id: string): Promise<CeebPushStatusResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/ceeb-push-status`, {
      method: 'GET',
      cache: 'no-store',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch CEEB push status');
    }
    return response.json();
  },

  // G12 (2026-10-19): trwające wysyłki wg status-file'ów backendu (GET /api/ceeb/push-running, tylko odczyt,
  // scope firmy) — lista po odświeżeniu panelu pokazuje „Wysyłanie…" i podpina polling ceebPushStatus.
  ceebPushRunning: async (): Promise<CeebPushRunningResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/ceeb/push-running`, { cache: 'no-store' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Nie udało się pobrać trwających wysyłek do CEEB');
    }
    return response.json();
  },

  // #53 (B9) — wystartuj rejestrację budynku w CEEB (dla budynku bez ceeb_id).
  // Backend: POST /api/buildings/:id/register-ceeb — spawnuje bota rejestracji i odpowiada
  // 202 { status:'started', jobId, buildingId, mode:'commit' }. Stan leci do pliku statusu
  // odczytywanego przez getRegisterCeebStatus(). 409 gdy budynek już ma ceeb_id lub
  // rejestracja już trwa — komunikat z backendu trafia do rzuconego Error.
  registerCeeb: async (id: string): Promise<RegisterCeebStartResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/register-ceeb`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to start CEEB registration');
    }
    return response.json();
  },

  // #53 (B9) — poll statusu rejestracji CEEB (no-store — zmienia się między pollami).
  // Backend: GET /api/buildings/:id/register-ceeb-status — idle|running|done|error, plus
  // ADDYTYWNIE registered/ceebId/ceebIdConflict, gdy skrypt bota dopisze. `finishedAt`
  // odróżnia świeży terminal od starego runu (anty-stale, jak przy import-status).
  getRegisterCeebStatus: async (id: string): Promise<RegisterCeebStatusResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/register-ceeb-status`, {
      method: 'GET',
      cache: 'no-store',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to fetch CEEB registration status');
    }
    return response.json();
  },

  // Zadanie 5.6 — start FAZY A (find-or-create nieruchomości i protokołu + prefill).
  // 202 = zadanie ruszyło; 409 = Faza A dla tego budynku już trwa (dwa równoległe przebiegi
  // mogłyby założyć dwa protokoły).
  startPhaseA: async (id: string): Promise<{ status: string; jobId: string; buildingId: string }> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/phase-a`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Nie udało się uruchomić Fazy A');
    }
    return response.json();
  },

  // Poll statusu Fazy A (no-store — zmienia się między pollami, krok po kroku).
  getPhaseAStatus: async (id: string): Promise<PhaseAStatusResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/phase-a-status`, {
      method: 'GET',
      cache: 'no-store',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Nie udało się pobrać statusu Fazy A');
    }
    return response.json();
  },

  // Podsumowania PDF per budynek (właściciel 2026-07-09). Backend (summaries.js) ma DWA
  // endpointy: 'inaccessible' (lokale nieudostępnione + odmowy) i 'findings' (nieprawidłowości
  // per lokal). UI zgubił do nich wejście przy konsolidacji nawigacji (#9). Pobieramy przez
  // authedFetch → Blob: przy AUTH_ENFORCE=1 goły <a href> dostałby 401 (brak nagłówka Bearer).
  // Kontrakt backendu bez zmian — to czysta konsumpcja istniejących tras.
  // #315: zwraca też nazwę z Content-Disposition — panel zapisuje plik pod nazwą z serwera.
  // `appendix` (kontrakt #338, backend PR #345): `?appendix=1` dokleja do „Podsumowania" osobną
  // stronę „Lokale – status końcowy i przebieg kontroli w lokalach". Tylko `findings`; pominięte =
  // bez dodatku (zachowanie domyślne, identyczne z poprzednim).
  // `heatSources` (Bronek 14.10, kontrakt-API): `?heatSources=1` = sekcja „Źródła ciepła";
  // pominięte = BEZ niej (backend domyślnie wydaje kopię dla zarządcy).
  downloadSummary: async (
    id: string,
    kind: 'inaccessible' | 'findings',
    opts?: SummaryDownloadOptions,
  ): Promise<DownloadedFile> => {
    const params = kind === 'findings' ? summaryOptionParams(opts) : [];
    const qs = params.length ? `?${params.join('&')}` : '';
    const response = await authedFetch(`${API_BASE_URL}/buildings/${id}/summary/${kind}.pdf${qs}`);
    if (!response.ok) {
      throw new Error(`Nie udało się pobrać podsumowania PDF (HTTP ${response.status})`);
    }
    return toDownloadedFile(response);
  },

  /**
   * E1 (§8, decyzja właściciela 2026-08-04): hurtowe „Nieprawidłowości" dla zaznaczonych
   * budynków — jeden ZIP, w środku OSOBNY plik per adres. Budynki niedostępne backend
   * pomija i raportuje (`pominiete.txt` w archiwum + nagłówek `X-Skipped-Count`), zamiast
   * wywracać całe pobranie; `skipped` oddajemy wołającemu, żeby UI mógł to powiedzieć wprost.
   */
  downloadSummariesZip: async (
    selection: string[] | SummariesZipFilters,
    opts?: SummaryDownloadOptions,
  ): Promise<{ blob: Blob; skipped: number; filename: string | null }> => {
    // E3: tablica = zaznaczenie (wygrywa nad filtrami); obiekt = paczka wg filtrów.
    // `appendix: true` (kontrakt #338) — strona „status i przebieg kontroli w lokalach" w każdym
    // podsumowaniu; `heatSources: true` — sekcja „Źródła ciepła". Pola wysyłamy TYLKO gdy włączone.
    const base = Array.isArray(selection) ? { buildingIds: selection } : selection;
    const body = {
      ...base,
      ...(opts?.appendix ? { appendix: true } : {}),
      ...(opts?.heatSources ? { heatSources: true } : {}),
    };
    const response = await authedFetch(`${API_BASE_URL}/buildings/summaries.zip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się pobrać podsumowań (HTTP ${response.status})`);
    }
    return {
      ...(await toDownloadedFile(response)),
      skipped: Number(response.headers.get('X-Skipped-Count') || 0),
    };
  },
};

// #52 (B8) — 409 z POST /inspections (guard duplikatu #46 w backend/src/routes/inspections.js):
// budynek ma już aktywną (nie-COMPLETED) inspekcję. Backend odpowiada
// { error, existingInspectionId, existingStatus }. `inspectionsApi.create` rzuca zwykły
// Error, ale DODATKOWO (additive) niesie te pola na instancji — istniejące catch(e) czytające
// tylko `e.message` działają bez zmian; UI, który chce zaproponować „Przejdź do istniejącego"
// (zamiast generycznego komunikatu/crasha), może odczytać `status`/`existingInspectionId`.
export interface InspectionConflictError extends Error {
  status?: number;
  existingInspectionId?: string;
  existingStatus?: InspectionStatus;
}

// W5 (kontrakt-API) — 409 z PUT /inspections/:id: próba zmiany first/second_term_date przy
// aktywnej (IN_PROGRESS) inspekcji bez potwierdzenia. Backend odpowiada
// { error, code:'ACTIVE_INSPECTION_TERMS' }. `inspectionsApi.update` rzuca zwykły Error, ale
// DODATKOWO niesie status/code (istniejące catch(e) czytające tylko e.message działają bez
// zmian; UI, które chce zapytać „przegląd w terenie — zmienić terminy?" i ponowić z
// rescheduleActive:true, odczyta code). Payload update rozszerzony o opcjonalne
// `rescheduleActive` — patrz `InspectionUpdatePayload` niżej.
export interface InspectionUpdateError extends Error {
  status?: number;
  code?: string;
}

/** PUT /buildings/:id — pola budynku + (058) źródła jako samo `{ id }` i wersje z odczytu. */
export type BuildingUpdatePayload = Partial<Omit<Building, 'heatSources'>> & {
  heatSources?: Array<Pick<HeatSource, 'id'> & Partial<HeatSource>>;
  heatSourcesBase?: Record<string, number>;
  noCommonHeatSourceBase?: boolean;
  /** 058: uuid jednej wysyłki (ponowienie tej samej treści nie zapisze jej drugi raz) — edycja parametrów źródła (B8). */
  writeId?: string;
};

// Payload PUT /inspections/:id. `rescheduleActive:true` = jawna zgoda na przełożenie terminów
// aktywnej inspekcji (omija guard #W5 po stronie backendu). Reszta pól jak Partial<Inspection>.
// 076 (kontrakt-API, addytywnie): `otherDeficienciesNoteBase` = treść dopisku „Inne uchybienia…", którą operator
// widział, otwierając edycję — inna w bazie → 409 code 'OTHER_DEFICIENCIES_NOTE_CHANGED' (body `current`).
export type InspectionUpdatePayload = Partial<Inspection> & { rescheduleActive?: boolean; otherDeficienciesNoteBase?: string };

async function queueRequest(path: 'queue' | 'queue/remove', buildingIds: string[]): Promise<InspectionQueueResponse> {
  const response = await authedFetch(`${API_BASE_URL}/inspections/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ buildingIds }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.error || (path === 'queue' ? 'Nie udało się dodać do kolejki' : 'Nie udało się usunąć z kolejki')) as InspectionUpdateError;
    e.status = response.status;
    if (err.code) e.code = err.code;
    throw e;
  }
  return response.json();
}

/**
 * 079 (zadanie 4 etap 2, kontrakt-API — nowe trasy): własny załącznik budynku — PDF kart mieszkań operatora, który bot
 * wgrywa do CEEB ZAMIAST naszych wygenerowanych kart. Za flagą serwera CEEB_OWN_CARDS_ATTACHMENT: wyłączona → GET
 * zwraca `enabled: false` (panel nie pokazuje uploadu), POST/DELETE/pobranie → 409 `FEATURE_DISABLED`.
 */
export interface UserCardsAttachment {
  id: string;
  inspectionId: string;
  /** Nazwa pliku wgranego przez operatora. */
  originalFilename: string | null;
  /** Nazwa, pod którą plik idzie do CEEB (`karty_mieszkan_<ulica>_<nr>_<sha8>[_vN].pdf`). */
  ceebFilename: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  createdAt: string;
  /** Ustawione po DOWODZIE z bota, że plik jest na liście załączników protokołu w CEEB. */
  ceebSyncedAt: string | null;
  /** Usunięty w panelu (na liście tylko, gdy był już wgrany do CEEB). */
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface UserCardsList {
  enabled: boolean;
  maxBytes: number;
  /** Plik, który pójdzie do CEEB przy najbliższej wysyłce (najnowszy aktywny); null = nasze karty. */
  activeId: string | null;
  attachments: UserCardsAttachment[];
}

export interface UserCardsUploadResult {
  attachment: UserCardsAttachment;
  /** Ten sam plik był już wgrany — bez duplikatu. */
  reused: boolean;
  /** Poprzednie, jeszcze niewysłane pliki zastąpione nowym. */
  replaced: string[];
}

async function userCardsError(response: Response, fallback: string): Promise<InspectionUpdateError> {
  const err = await response.json().catch(() => ({} as any));
  const e = new Error((err as any).error || fallback) as InspectionUpdateError;
  e.status = response.status;
  if ((err as any).code) e.code = (err as any).code;
  return e;
}

export const userCardsApi = {
  list: async (inspectionId: string): Promise<UserCardsList> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${inspectionId}/user-cards`, { cache: 'no-store' });
    if (!response.ok) throw await userCardsError(response, `Nie udało się odczytać załączników (HTTP ${response.status})`);
    return response.json();
  },
  upload: async (inspectionId: string, file: File): Promise<UserCardsUploadResult> => {
    const fd = new FormData();
    fd.append('file', file, file.name);
    // Bez Content-Type — przeglądarka sama dopisze multipart z granicą (boundary).
    const response = await authedFetch(`${API_BASE_URL}/inspections/${inspectionId}/user-cards`, { method: 'POST', body: fd });
    if (!response.ok) throw await userCardsError(response, `Nie udało się wgrać pliku (HTTP ${response.status})`);
    return response.json();
  },
  download: async (inspectionId: string, attachmentId: string): Promise<DownloadedFile> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${inspectionId}/user-cards/${attachmentId}/file`);
    if (!response.ok) throw await userCardsError(response, `Nie udało się pobrać pliku (HTTP ${response.status})`);
    return toDownloadedFile(response);
  },
  remove: async (inspectionId: string, attachmentId: string): Promise<{ attachment: UserCardsAttachment }> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${inspectionId}/user-cards/${attachmentId}`, { method: 'DELETE' });
    if (!response.ok) throw await userCardsError(response, `Nie udało się usunąć pliku (HTTP ${response.status})`);
    return response.json();
  },
};

export const inspectionsApi = {
  // Get all inspections (optionally filtered by status or building)
  getAll: async (filters?: { status?: InspectionStatus; buildingId?: string }): Promise<Inspection[]> => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.buildingId) params.append('building_id', filters.buildingId);

    const url = `${API_BASE_URL}/inspections${params.toString() ? '?' + params.toString() : ''}`;
    const response = await authedFetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch inspections');
    }
    return response.json();
  },

  // Get single inspection by ID
  getById: async (id: string): Promise<Inspection> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${id}`);
    if (!response.ok) {
      throw new Error('Failed to fetch inspection');
    }
    return response.json();
  },

  // Create new inspection.
  // Backend: POST /api/inspections — 409 (patrz InspectionConflictError powyżej) gdy budynek
  // ma już aktywną inspekcję (#46/#52). Reszta błędów: fallback do generycznego Error.
  // A12 (kontrakt-API): opcjonalne `apartmentCount` (1..500) — lokale 1..N, gdy budynek nie ma lokali;
  // odpowiedź niesie wtedy `apartmentsAdded` + `apartmentsNote` (bez pola — odpowiedź jak dotąd).
  // 078 (kontrakt-API): opcjonalne `prefillSourceCase` (NR/ROK z GET /buildings/:id/protocols) ALBO `prefillSourceYear`
  // (planowanie zbiorcze — protokół z roku wybiera serwer); odpowiedź niesie wtedy `prefillSource` + `prefillSourceNote`.
  create: async (
    inspection: Omit<Inspection, 'id' | 'createdAt' | 'updatedAt'> & {
      apartmentCount?: number; prefillSourceCase?: string | null; prefillSourceYear?: number;
    },
  ): Promise<Inspection & { apartmentsAdded?: string[]; apartmentsNote?: string } & PrefillSourceResult> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inspection),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const conflictErr = new Error(err.error || 'Failed to create inspection') as InspectionConflictError;
      conflictErr.status = response.status;
      if (err.existingInspectionId) conflictErr.existingInspectionId = err.existingInspectionId;
      if (err.existingStatus) conflictErr.existingStatus = err.existingStatus;
      throw conflictErr;
    }
    return response.json();
  },

  // Update inspection. Payload może nieść `rescheduleActive:true` (W5 — zgoda na zmianę
  // terminów aktywnej inspekcji). Na !ok rzuca Error wzbogacony o status/code
  // (InspectionUpdateError): 409 + code:'ACTIVE_INSPECTION_TERMS' → UI proponuje potwierdzenie.
  update: async (id: string, inspection: InspectionUpdatePayload): Promise<Inspection> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inspection),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error(err.error || 'Failed to update inspection') as InspectionUpdateError;
      e.status = response.status;
      if (err.code) e.code = err.code;
      throw e;
    }
    return response.json();
  },

  // 078 (kontrakt-API): PUT /inspections/:id/prefill-source — protokół CEEB, z którego import bierze dane przeglądu.
  // `{ caseNumber: null }` = protokół bieżący; `{ year }` bez protokołu z roku nie zmienia wyboru (`changed:false` + notka).
  // 400 PREFILL_SOURCE_INVALID/UNKNOWN, 409 PREFILL_SOURCE_LOCKED (przegląd zamknięty/wysłany do CEEB albo nie
  // najnowszy przegląd budynku) — rzuca Error z `status`/`code`.
  setPrefillSource: async (
    id: string,
    body: { caseNumber: string | null } | { year: number },
  ): Promise<PrefillSourceResult & {
    id: string; prefillSourceCase: string | null; changed: boolean;
    /** Pola przeglądu już wypełnione (import wpisuje tylko w puste) — zmiana źródła ich nie przepisze. */
    prefillFieldsAlreadySet?: string[];
  }> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${id}/prefill-source`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error(err.error || 'Nie udało się zapisać źródła danych z CEEB') as InspectionUpdateError;
      e.status = response.status;
      if (err.code) e.code = err.code;
      throw e;
    }
    return response.json();
  },

  // Delete inspection
  // Odplanowanie (pkt 24, 2026-09-13): backend odrzuca 409 z code INSPECTION_HAS_VISITS
  // (zapisy lokali) / INSPECTION_IN_CEEB (protokół w CEEB) — komunikat ma dojść do UI.
  delete: async (id: string): Promise<void> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const e = new Error(err.error || 'Failed to delete inspection') as InspectionUpdateError;
      e.status = response.status;
      if (err.code) e.code = err.code;
      throw e;
    }
  },

  // Migr. 073 (właściciel 21.10, „Zaplanuj do kolejki”): „Do kolejki” / „Usuń z kolejki” — pojedynczo i zbiorczo.
  // Backend: POST /api/inspections/queue | /queue/remove { buildingIds } → 200 z wynikiem per budynek (odmowy w `skipped`
  // z kodem i powodem po polsku); 400 tylko przy złym body.
  queue: async (buildingIds: string[]): Promise<InspectionQueueResponse> => queueRequest('queue', buildingIds),
  unqueue: async (buildingIds: string[]): Promise<InspectionQueueResponse> => queueRequest('queue/remove', buildingIds),

  // Add an additional inspection term at the BUILDING level (III, IV...) —
  // used when tenants are absent/refuse across the 2 base terms.
  // Backend: POST /api/inspections/:id/additional-terms — appends { date, addedAt, reason }
  // to inspections.additional_terms JSONB.
  // Does NOT touch firstTermDate/secondTermDate.
  //
  // ⚠️ POPRAWKA KONTRAKTU 2026-07-28: ten typ deklarował `additional_terms` (snake_case),
  // a backend od zawsze zwracał `additionalTerms`. TypeScript nie miał więc jak złapać
  // `result.additional_terms === undefined` w BuildingInspectionView — lista dodatkowych
  // terminów znikała po dodaniu wpisu, aż do pierwszego refetchu.
  // `terms` = przenumerowana lista wszystkich terminów (numeruje backend).
  addAdditionalTerm: async (
    id: string,
    data: { date: string; reason?: string }
  ): Promise<AdditionalTermsResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${id}/additional-terms`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to add additional term');
    }
    return response.json();
  },

  // Delete an inspection term by its SLOT NUMBER (U6, 2026-07-29).
  // Backend: DELETE /api/inspections/:id/terms/:number — 1 clears first_term_date,
  // 2 clears second_term_date, ≥3 removes the matching entry from additional_terms.
  // ⚠️ `number` to NUMER TERMINU (InspectionTerm.number), nie indeks w tablicy. Poprzednia
  // wersja kasowała po `additionalIndex`, przez co termin w slocie I/II był nieusuwalny —
  // tworzenie routowało po slocie, a kasowanie nie (patrz issue kontrakt-API).
  // Backend odrzuca 409 TERM_HAS_VISITS, gdy w terminie są już zapisane wizyty.
  deleteTerm: async (
    id: string,
    number: number
  ): Promise<AdditionalTermsResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/inspections/${id}/terms/${number}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to delete term');
    }
    return response.json();
  },

  // Protokół kontroli (PDF) — TEN SAM dokument, który mobilka generuje przy „Generuj PDF"
  // (feedback właściciela 2026-07-24); backend odtwarza go z danych przez puppeteer. Bez
  // apartmentNumbers → wszystkie lokale (jeden PDF); 1 numer → pojedynczy; wiele → połączony.
  // #315: zwraca też nazwę z Content-Disposition (`protokol_<ulica>_<nr>….pdf`).
  downloadProtocols: async (inspectionId: string, apartmentNumbers?: string[]): Promise<DownloadedFile> => {
    const q = apartmentNumbers && apartmentNumbers.length
      ? `?apartments=${encodeURIComponent(apartmentNumbers.join(','))}`
      : '';
    const response = await authedFetch(`${API_BASE_URL}/inspections/${inspectionId}/protocols.pdf${q}`);
    if (!response.ok) {
      const txt = await response.text().catch(() => '');
      throw new Error(txt || `Nie udało się pobrać protokołu PDF (HTTP ${response.status})`);
    }
    return toDownloadedFile(response);
  },
  // #416 (lista Bronka 12.10): karty lokali z podpisami lokatorów + podsumowanie — układ dokumentu,
  // który idzie do CEEB jako załącznik, generowany na żywo (GET /inspections/:id/cards.pdf).
  // `heatSources` (Bronek 14.10): `?heatSources=1` = podsumowanie ze „Źródłami ciepła"; pominięte = bez.
  downloadCards: async (inspectionId: string, opts?: { heatSources?: boolean }): Promise<DownloadedFile> => {
    const qs = opts?.heatSources ? '?heatSources=1' : '';
    const response = await authedFetch(`${API_BASE_URL}/inspections/${inspectionId}/cards.pdf${qs}`);
    if (!response.ok) {
      const txt = await response.text().catch(() => '');
      throw new Error(response.status === 404
        ? 'Brak kart — żaden lokal nie ma jeszcze zapisanej kontroli'
        : (txt || `Nie udało się pobrać kart lokali (HTTP ${response.status})`));
    }
    return toDownloadedFile(response);
  },
};

// Clients API
export interface Client {
  id: string;
  name: string;
  // 055: skrócona nazwa (Complex / Avente / ZBM…) do kolumny „Zarządca" w Budynkach; null = brak.
  // W PUT: brak pola = bez zmian, '' = wyczyszczenie.
  shortName?: string | null;
  nip?: string;
  address: string;
  phone: string;
  email: string;
  inspectionRate: number;
  ceebRate: number;
  // 068 (C3, kontrakt-API): kategoria zarządcy (PRYWATNE / JEDNORODZINNE / FIRMY / … + własne);
  // null = bez kategorii. `companyId` — firma zarządcy (lista kategorii jest per firma).
  companyId?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  // 068 (C4): pola WEWNĘTRZNE — tylko panel, nigdy protokół/PDF/CEEB/mail.
  internalNote?: string | null;
  /** 'per_apartment' = ryczałt za lokal (stawka za przegląd lokalu), 'flat' = jedna cena za całość. */
  priceMode?: ClientPriceMode | null;
  priceFlat?: number | null;
  buildingsCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export type ClientPriceMode = 'per_apartment' | 'flat';

/**
 * Body POST/PUT /api/clients. Konwencja pól 055/068 (shortName, categoryId, internalNote, priceMode,
 * priceFlat): brak pola = bez zmian, '' = wyczyszczenie.
 */
export type ClientWrite = Omit<Client, 'id' | 'createdAt' | 'updatedAt' | 'buildingsCount' | 'companyId'
  | 'categoryName' | 'categoryId' | 'internalNote' | 'priceMode' | 'priceFlat'> & {
  categoryId?: string;
  internalNote?: string;
  priceMode?: ClientPriceMode | '';
  priceFlat?: number | '';
};

/** 068 (C3): kategoria zarządców (per firma). Wyłączona (`active: false`) znika z list wyboru. */
export interface ClientCategory {
  id: string;
  companyId: string | null;
  name: string;
  sortOrder: number;
  active: boolean;
  clientsCount?: number;
}

export const clientsApi = {
  getAll: async (): Promise<Client[]> => {
    const response = await authedFetch(`${API_BASE_URL}/clients`);
    if (!response.ok) throw new Error('Failed to fetch clients');
    return response.json();
  },

  create: async (client: ClientWrite): Promise<Client> => {
    const response = await authedFetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(client),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to create client');
    }
    return response.json();
  },

  update: async (id: string, client: Partial<ClientWrite>): Promise<Client> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(client),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || 'Failed to update client');
    }
    return response.json();
  },

  // detach=true: świadome „usuń i odepnij budynki" (po ConfirmModal); bez flagi backend
  // odmawia 409, gdy zarządca ma budynki (spec 2026-08-10).
  delete: async (id: string, opts?: { detach?: boolean }): Promise<void> => {
    const qs = opts?.detach ? '?detach=1' : '';
    const response = await authedFetch(`${API_BASE_URL}/clients/${id}${qs}`, { method: 'DELETE' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error(err.error || 'Failed to delete client') as Error & { status?: number; buildingsCount?: number };
      e.status = response.status;
      e.buildingsCount = err.buildingsCount;
      throw e;
    }
  },

  getBuildings: async (id: string): Promise<ClientBuilding[]> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/${id}/buildings`);
    if (!response.ok) throw new Error('Failed to fetch client buildings');
    return response.json();
  },

  // Wyszukiwarka adresów do hurtowego przypisywania (właściciel 2026-09-15); backend: GET /api/clients/search-buildings?q=
  searchBuildings: async (q: string): Promise<ClientBuildingSearchHit[]> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/search-buildings?q=${encodeURIComponent(q)}`);
    if (!response.ok) throw new Error('Nie udało się wyszukać budynków');
    return response.json();
  },

  // Hurtowe przypisanie budynków BEZ zarządcy; cudze wracają w `skipped` z nazwą zarządcy.
  // `reassign: true` (właściciel 2026-10-02) ŚWIADOMIE przepina też budynki innego zarządcy —
  // wołać dopiero po ConfirmModal, który pokazuje, które budynki i od kogo.
  attachBuildings: async (id: string, payload: { buildingIds: string[]; reassign?: boolean }): Promise<AttachBuildingsResult> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/${id}/attach-buildings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Nie udało się przypisać budynków');
    }
    return response.json();
  },

  // 068 (C3): kategorie zarządców. Odczyt — każdy zalogowany; zapis — admin/superadmin.
  getCategories: async (): Promise<ClientCategory[]> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/categories`);
    if (!response.ok) throw new Error('Nie udało się pobrać kategorii zarządców');
    return response.json();
  },

  createCategory: async (name: string): Promise<ClientCategory> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err.error || 'Nie udało się dodać kategorii');
    }
    return response.json();
  },

  // Pole pominięte zostaje; kategorii się nie kasuje — `active: false` zdejmuje ją z list wyboru.
  updateCategory: async (id: string, patch: { name?: string; active?: boolean }): Promise<ClientCategory> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/categories/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err.error || 'Nie udało się zapisać kategorii');
    }
    return response.json();
  },

  reorderCategories: async (order: string[]): Promise<{ updated: number }> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/categories/order`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err.error || 'Nie udało się zmienić kolejności kategorii');
    }
    return response.json();
  },

  // Hurtowe przepinanie budynków zarządcy; targetClientId=null odpina.
  moveBuildings: async (
    id: string,
    payload: { targetClientId: string | null; buildingIds: string[] },
  ): Promise<{ moved: number }> => {
    const response = await authedFetch(`${API_BASE_URL}/clients/${id}/move-buildings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error(err.error || 'Failed to move client buildings');
    }
    return response.json();
  },
};

export interface ClientBuilding {
  id: string;
  street: string;
  houseNumber: string;
  city: string;
  type?: string | null;
}

/** Trafienie wyszukiwarki adresów do przypisywania budynków zarządcy (2026-09-15). */
export interface ClientBuildingSearchHit {
  id: string;
  street: string | null;
  houseNumber: string | null;
  city: string | null;
  clientId: string | null;
  clientName: string | null;
}

export interface AttachBuildingsResult {
  attached: number;
  /** Ile z `attached` miało wcześniej INNEGO zarządcę (tylko przy reassign:true; addytywne). */
  reassigned?: number;
  skipped: Array<ClientBuildingSearchHit & { reason: 'already-attached' | 'other-client' }>;
}

/** Odpowiedź PUT /api/apartments/labels — stan oznaczeń wysłanych lokali po zapisie (E3, migr. 071). */
export interface ApartmentLabelsResult {
  updated: number;
  apartments: { id: string; apartmentNumber: string; displayLabel: string | null; staircase: string | null }[];
}

/** Pozycja zmiany numeru lokalu (POST /api/apartments/renumber, kontrakt-API — zmiana numerów lokali 2026-10-21). */
export interface ApartmentRenumberItem {
  apartmentId: string;
  /** Numer widziany w panelu — serwer odmawia (409 APARTMENTS_CHANGED), gdy w bazie jest już inny. */
  from: string;
  to: string;
  /** Wyczyść „Oznaczenie na drzwiach" równe NOWEMU numerowi (np. „32" na lokalu 32); inne oznaczenia zostają. */
  clearDisplayLabel?: boolean;
}

/** Bezpiecznik zmiany numerów (lokal albo cały budynek — apartmentId null). */
export interface ApartmentRenumberBlock {
  apartmentId: string | null;
  apartmentNumber: string | null;
  code: string;
  error: string;
}

/** Odpowiedź podglądu (`dryRun: true`). */
export interface ApartmentRenumberPreview {
  dryRun: true;
  allowed: boolean;
  blocked: ApartmentRenumberBlock[];
  changes: Array<{
    apartmentId: string; from: string; to: string;
    displayLabel: string | null; labelRedundant: boolean; clearDisplayLabel: boolean;
  }>;
  /** Zwolnione numery — nagrobek: nocny import nie założy ich ponownie z protokołów z lat poprzednich. */
  vacated: string[];
  /** Nowe numery, które miały nagrobek (lokal usunięty w aplikacji) — nagrobek zostanie zdjęty. */
  reclaimed: string[];
}

export interface ApartmentRenumberResult {
  ok: true;
  renumbered: number;
  apartments: Array<{ id: string; apartmentNumber: string; displayLabel: string | null }>;
  tombstonesCreated: string[];
  tombstonesRemoved: string[];
  recordsUpdated: number;
}

/** Błąd trasy zmiany numerów: komunikat dla UI + `code` (400/409) i lista bezpieczników przy 409. */
export type ApartmentRenumberError = Error & { status?: number; code?: string; blocked?: ApartmentRenumberBlock[] };

// Apartments API
/** GET /apartments/:id/delete-with-records-check — podgląd usunięcia lokalu z zapisami (bez podpisów i danych lokatora). */
export interface ApartmentDeleteWithRecordsCheck {
  apartmentId: string;
  apartmentNumber: string;
  buildingId: string;
  address: string;
  allowed: boolean;
  /** Powód odmowy: APARTMENT_RECORD_IN_CEEB, PROTOCOL_ISSUED, APARTMENT_IN_CEEB, APARTMENT_CEEB_UNCHECKED, TABLET_PENDING,
   *  NO_RECORDS, PUSH_IN_PROGRESS / ACCEPT_IN_PROGRESS / PHASE_A_IN_PROGRESS (Issue #456). */
  blocked: { code: string; error: string } | null;
  /** Zapisy kontroli, które zostaną usunięte: status kanoniczny + data wizyty 'YYYY-MM-DD'. */
  records: Array<{ id: string; inspectionId: string | null; status: string; date: string | null; version: string }>;
  heatSources: number;
}

export interface ApartmentDeleteWithRecordsResult {
  ok: true;
  id: string;
  apartmentNumber: string;
  buildingId: string;
  recordsDeleted: number;
}

export const apartmentsApi = {
  /**
   * Bronek 2026-09-15 (Bytomska 100): usunięcie lokalu dodanego przez pomyłkę. DELETE /api/apartments/:id;
   * backend odrzuca 409 z `code`: APARTMENT_HAS_RECORDS (zapisy kontroli) / APARTMENT_IN_CEEB (lokal
   * istnieje w protokole CEEB — import odtworzyłby go rano). Komunikat ma dojść do UI.
   */
  delete: async (apartmentId: string): Promise<void> => {
    const response = await authedFetch(`${API_BASE_URL}/apartments/${apartmentId}`, { method: 'DELETE' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error((err as any).error || `Nie udało się usunąć lokalu (HTTP ${response.status})`) as Error & { status?: number; code?: string };
      e.status = response.status;
      if ((err as any).code) e.code = (err as any).code;
      throw e;
    }
  },

  /**
   * E3 (raport uwag Bronka 2026-10-19; migr. 071; kontrakt-API): oznaczenia lokali do WYŚWIETLANIA — drzwi
   * (`displayLabel`) i klatka (`staircase`), pojedynczo albo hurtowo. PUT /api/apartments/labels. Pole pominięte = bez
   * zmian, `null`/'' = wyczyść. Numer lokalu (tożsamość w CEEB) się nie zmienia. 400 (walidacja / lokal spoza
   * budynku, `code: APARTMENT_NOT_IN_BUILDING`) — nic nie zapisane; komunikat ma dojść do UI.
   */
  updateLabels: async (
    buildingId: string,
    items: { apartmentId: string; displayLabel?: string | null; staircase?: string | null }[],
  ): Promise<ApartmentLabelsResult> => {
    const response = await authedFetch(`${API_BASE_URL}/apartments/labels`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buildingId, items }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się zapisać oznaczeń lokali (HTTP ${response.status})`);
    }
    return response.json();
  },
  /**
   * Zmiana NUMERÓW lokali (prośba właściciela 2026-10-21, Wolności 532c 1–10 → 31–40; kontrakt-API Issue #477; admin/superadmin).
   * POST /api/apartments/renumber — `dryRun: true` = podgląd (`ApartmentRenumberPreview`, zawsze 200 przy poprawnym planie,
   * bezpieczniki w `blocked`), bez = zapis w jednej transakcji (`ApartmentRenumberResult`). Błąd → `ApartmentRenumberError`
   * z `code` (NUMBER_TAKEN, APARTMENTS_CHANGED, LABEL_NOT_REDUNDANT, APARTMENT_IN_CEEB, TABLET_PENDING, …) — komunikat do UI.
   */
  renumber: async <D extends boolean>(
    buildingId: string,
    items: ApartmentRenumberItem[],
    dryRun: D,
  ): Promise<D extends true ? ApartmentRenumberPreview : ApartmentRenumberResult> => {
    const response = await authedFetch(`${API_BASE_URL}/apartments/renumber`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buildingId, items, ...(dryRun ? { dryRun: true } : {}) }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error((err as any).error || `Nie udało się zmienić numerów lokali (HTTP ${response.status})`) as ApartmentRenumberError;
      e.status = response.status;
      if ((err as any).code) e.code = (err as any).code;
      if (Array.isArray((err as any).blocked)) e.blocked = (err as any).blocked;
      throw e;
    }
    return response.json();
  },
  /**
   * F8 / B5 reszta (raport uwag Bronka 2026-10-19; TYLKO superadmin, 403 dla innych): co zniknie przy usunięciu lokalu
   * RAZEM z zapisem kontroli i czy wolno (zawsze 200 dla istniejącego lokalu: `allowed` + `blocked`).
   */
  deleteWithRecordsCheck: async (apartmentId: string): Promise<ApartmentDeleteWithRecordsCheck> => {
    const response = await authedFetch(`${API_BASE_URL}/apartments/${apartmentId}/delete-with-records-check`, { cache: 'no-store' });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się sprawdzić lokalu (HTTP ${response.status})`);
    return response.json();
  },
  /**
   * Usunięcie lokalu z zapisami — `records` = zapisy pokazane w potwierdzeniu (id + `version` z podglądu); inna lista
   * albo zmieniona od tego czasu treść zapisu na serwerze → 409 RECORDS_CHANGED.
   */
  deleteWithRecords: async (
    apartmentId: string,
    records: Array<{ id: string; version: string }>,
  ): Promise<ApartmentDeleteWithRecordsResult> => {
    const response = await authedFetch(`${API_BASE_URL}/apartments/${apartmentId}/delete-with-records`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ records: records.map(({ id, version }) => ({ id, version })) }),
    });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się usunąć lokalu (HTTP ${response.status})`);
    return response.json();
  },

  // Update apartment status (partial update - doesn't regenerate UUID)
  updateStatus: async (apartmentId: string, data: {
    inspectionStatus?: string;
    lastInspectionDate?: string;
    nextInspectionDate?: string;
  }): Promise<any> => {
    const response = await authedFetch(`${API_BASE_URL}/apartments/${apartmentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error('Failed to update apartment status');
    }
    return response.json();
  },
};

/** Błąd POST /apartment-inspections. 409 RECORD_CHANGED niesie `record` — rekord z serwera (kształt GET). */
export interface ApartmentRecordSaveError extends Error {
  status?: number;
  code?: string;
  record?: any;
}

// Apartment Inspection Records API
export const apartmentInspectionsApi = {
  // Get all inspection records for a specific apartment
  getByApartmentId: async (apartmentId: string): Promise<any[]> => {
    const response = await authedFetch(`${API_BASE_URL}/apartment-inspections/apartment/${apartmentId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch apartment inspection records');
    }
    return response.json();
  },

  // Get all inspection records for an inspection period
  getByInspectionId: async (inspectionId: string): Promise<any[]> => {
    const response = await authedFetch(`${API_BASE_URL}/apartment-inspections/inspection/${inspectionId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch inspection records');
    }
    return response.json();
  },

  // Create or update inspection record
  // N1 w panelu (2026-10-10, kontrakt-API #401): `data.expectedUpdatedAt` = wersja rekordu, którą formularz
  // wczytał (jak tablet). Gdy ktoś zapisał później, serwer odpowiada 409 code:'RECORD_CHANGED' z `record`
  // (wersja z serwera) i nic nie zapisuje — błąd niesie status/code/record (ApartmentRecordSaveError).
  // Źródła ciepła lokalu z panelu (kontrakt-API #416 pkt 3, 2026-10-12): `heatSources` (CAŁA lista w kształcie
  // tabletu { id, type, heatCharacter, params } — serwer podmienia tablicę), `usesBuildingHeat`, `noHeatSource`.
  // Formularz wysyła je TYLKO, gdy admin je zmienił; pole pominięte = serwer zachowuje wartość (COALESCE #57).
  createOrUpdate: async (data: any): Promise<any> => {
    const response = await authedFetch(`${API_BASE_URL}/apartment-inspections`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error(err.error || 'Failed to create/update inspection record') as ApartmentRecordSaveError;
      e.status = response.status;
      if (err.code) e.code = err.code;
      if (err.record) e.record = err.record;
      throw e;
    }
    return response.json();
  },

  // Zbiorcza nieobecność z panelu (Bronek 2026-10-14, kontrakt-API): POST /apartment-inspections/bulk-absence.
  // Każdy lokal osobno — oznaczony albo pominięty z powodem (wynik kontroli, ta sama nieobecność, zmiana po
  // otwarciu okna = `expectedUpdatedAt` starszy niż wersja na serwerze). Zła data → 400 code INVALID_ABSENCE_DATE.
  bulkAbsence: async (data: BulkAbsenceRequest): Promise<BulkAbsenceResult> => {
    const response = await authedFetch(`${API_BASE_URL}/apartment-inspections/bulk-absence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      const e = new Error(err.error || 'Nie udało się oznaczyć nieobecności') as Error & { status?: number; code?: string };
      e.status = response.status;
      if (err.code) e.code = err.code;
      throw e;
    }
    return response.json();
  },

};

/** Body POST /apartment-inspections/bulk-absence. `date` = dzień nieobecności 'YYYY-MM-DD'. */
export interface BulkAbsenceRequest {
  inspectionId: string;
  date: string;
  /** `expectedUpdatedAt` = wersja rekordu widziana w oknie; bez rekordu — NO_RECORD_VERSION (utils/recordConflict). */
  apartments: { apartmentId: string; expectedUpdatedAt?: string }[];
}

/**
 * Wynik zbiorczej nieobecności. `connectedDefault` = lokal dostał domyślne „podłączony do wspólnego źródła"
 * (budynek z siecią / wspólnym kotłem C.O., rekord bez informacji o źródłach — jak na tablecie).
 * `code` pominięcia: HAS_RESULT | ALREADY_RECORDED | RECORD_CHANGED | NOT_IN_BUILDING | ERROR.
 */
export interface BulkAbsenceResult {
  date: string;
  termNumber: number | null;
  marked: { apartmentId: string; apartmentNumber: string; recordId: string; created: boolean; connectedDefault: boolean }[];
  skipped: { apartmentId: string; apartmentNumber: string | null; code: string; reason: string }[];
}

// --- Edytowalny katalog zaleceń budynkowych A/B/C/D (migr. 034; feedback 2026-07-24) ------
// Strona „Zalecenia" w adminie edytuje treści (full_text), które zasilają checkboxy na
// tablecie kominiarzy i teksty wpisywane przez bota do CEEB.
export interface CatalogRecommendationItem {
  code: string;
  shortLabel: string;
  fullText: string;
  isGroup: boolean;
  subItems?: CatalogRecommendationItem[];
}

/** Płaska pozycja katalogu — odpowiedź POST/PUT/PATCH (bez `subItems`, za to z hierarchią). */
export interface CatalogItemDetail {
  code: string;
  category: string;
  parentCode: string | null;
  isGroup: boolean;
  shortLabel: string;
  fullText: string;
  sortOrder: number;
}

export const recommendationsApi = {
  getCatalog: async (): Promise<{ categories: { id: string; items: CatalogRecommendationItem[] }[] }> => {
    const response = await authedFetch(`${API_BASE_URL}/recommendations/catalog`);
    if (!response.ok) throw new Error(`Nie udało się pobrać katalogu zaleceń (HTTP ${response.status})`);
    return response.json();
  },
  updateItem: async (
    code: string,
    data: { shortLabel?: string; fullText?: string },
  ): Promise<CatalogItemDetail> => {
    const response = await authedFetch(`${API_BASE_URL}/recommendations/catalog/${encodeURIComponent(code)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || 'Nie udało się zapisać zalecenia');
    }
    return response.json();
  },
  /** Nowa pozycja albo grupa (blok C, 2026-08-04); kod nadaje SERWER i jest stały. */
  createItem: async (data: {
    category: string;
    shortLabel: string;
    fullText?: string;
    parentCode?: string | null;
    isGroup?: boolean;
    /** WYMAGANE dla grupy — pusta grupa jest na tablecie zaznaczalnym checkboxem bez treści. */
    firstItem?: { shortLabel: string; fullText: string };
  }): Promise<CatalogItemDetail & { subItems?: CatalogItemDetail[] }> => {
    const response = await authedFetch(`${API_BASE_URL}/recommendations/catalog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || 'Nie udało się dodać pozycji');
    }
    return response.json();
  },
  /** Grupowanie: `parentCode` = kod grupy, `null` = wyjęcie na poziom rubryki. Kod pozycji ZOSTAJE. */
  moveToGroup: async (code: string, parentCode: string | null): Promise<CatalogItemDetail> => {
    const response = await authedFetch(
      `${API_BASE_URL}/recommendations/catalog/${encodeURIComponent(code)}/group`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ parentCode }),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || 'Nie udało się zmienić grupy');
    }
    return response.json();
  },

  // --- Listy „Typ urządzenia" zaleceń per-lokal (migr. 054; Bronek 2026-10-02) ---------------
  // GET: każdy zalogowany (WSZYSTKIE pozycje, także active=false); zapis: tylko superadmin
  // (admin → 403; D9 2026-10-10, kontrakt-API #406).
  // Kontrakt-API: additive (nowe endpointy). Brak DELETE — wycofanie = active:false.
  getDeviceOptions: async (): Promise<{ sets: Record<string, DeviceOptionDetail[]> }> => {
    const response = await authedFetch(`${API_BASE_URL}/recommendations/device-options`);
    if (!response.ok) throw new Error(`Nie udało się pobrać list urządzeń (HTTP ${response.status})`);
    return response.json();
  },
  createDeviceOption: async (
    recommendationId: string,
    data: { label: string; protocolText?: string | null },
  ): Promise<DeviceOptionDetail> => {
    const response = await authedFetch(
      `${API_BASE_URL}/recommendations/device-options/${encodeURIComponent(recommendationId)}`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || 'Nie udało się dodać pozycji');
    }
    return response.json();
  },
  /** Pole pominięte zostaje; `protocolText: null` czyści skrót (do treści idzie wtedy nazwa). */
  updateDeviceOption: async (
    recommendationId: string,
    optionId: string,
    data: { label?: string; protocolText?: string | null; active?: boolean },
  ): Promise<DeviceOptionDetail> => {
    const response = await authedFetch(
      `${API_BASE_URL}/recommendations/device-options/${encodeURIComponent(recommendationId)}/${encodeURIComponent(optionId)}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || 'Nie udało się zapisać pozycji');
    }
    return response.json();
  },
  /** `order` = WSZYSTKIE id zestawu w nowej kolejności (także nieaktywne). */
  reorderDeviceOptions: async (
    recommendationId: string,
    order: string[],
  ): Promise<{ options: DeviceOptionDetail[] }> => {
    const response = await authedFetch(
      `${API_BASE_URL}/recommendations/device-options/${encodeURIComponent(recommendationId)}/order`,
      { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ order }) },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || 'Nie udało się zmienić kolejności');
    }
    return response.json();
  },

  // --- Katalog zaleceń PER LOKAL (migr. 059; Bronek 2026-10-07/09) -------------------------
  // GET: każdy zalogowany (WSZYSTKIE pozycje, także wycofane); zapis: tylko superadmin
  // (admin → 403; D9 2026-10-10, kontrakt-API #406).
  // Kontrakt-API: additive (nowe endpointy). Brak DELETE — wycofanie = active:false.
  getApartmentItems: async (): Promise<{ items: ApartmentRecommendationItemDetail[] }> => {
    const response = await authedFetch(`${API_BASE_URL}/recommendations/apartment-items`);
    if (!response.ok) throw new Error(`Nie udało się pobrać zaleceń lokali (HTTP ${response.status})`);
    return response.json();
  },
  /** `allowOverlap: true` = zapis mimo ostrzeżenia o podobnej treści (409 TEXT_OVERLAP). */
  createApartmentItem: async (
    data: ApartmentRecommendationItemInput & { label: string; baseText: string; allowOverlap?: boolean },
  ): Promise<ApartmentRecommendationItemDetail> => {
    const response = await authedFetch(`${API_BASE_URL}/recommendations/apartment-items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw await apartmentItemError(response, 'Nie udało się dodać zalecenia');
    return response.json();
  },
  /** Pole pominięte zostaje. Zmiana `baseText` dopisuje poprzednią treść do `legacyTexts`. */
  updateApartmentItem: async (
    id: string,
    data: ApartmentRecommendationItemInput & { active?: boolean; allowOverlap?: boolean },
  ): Promise<ApartmentRecommendationItemDetail> => {
    const response = await authedFetch(
      `${API_BASE_URL}/recommendations/apartment-items/${encodeURIComponent(id)}`,
      { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) },
    );
    if (!response.ok) throw await apartmentItemError(response, 'Nie udało się zapisać zalecenia');
    return response.json();
  },
  /** `order` = WSZYSTKIE id katalogu w nowej kolejności (także wycofane). */
  reorderApartmentItems: async (order: string[]): Promise<{ items: ApartmentRecommendationItemDetail[] }> => {
    const response = await authedFetch(`${API_BASE_URL}/recommendations/apartment-items/order`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order }),
    });
    if (!response.ok) throw await apartmentItemError(response, 'Nie udało się zmienić kolejności');
    return response.json();
  },
};

/** Błąd zapisu katalogu zaleceń lokali — `code` = 'TEXT_OVERLAP' (podobna treść) itp. */
export class ApartmentItemError extends Error {
  code?: string;

  conflicts?: { id: string; label: string }[];

  constructor(message: string, code?: string, conflicts?: { id: string; label: string }[]) {
    super(message);
    this.code = code;
    this.conflicts = conflicts;
  }
}

async function apartmentItemError(response: Response, fallback: string): Promise<ApartmentItemError> {
  const err = await response.json().catch(() => ({} as any));
  return new ApartmentItemError((err as any).error || fallback, (err as any).code, (err as any).conflicts);
}

export type ApartmentRecommendationMode = 'none' | 'optional' | 'required';

/** Pola edytowalne pozycji katalogu zaleceń lokali (POST/PATCH /recommendations/apartment-items). */
export interface ApartmentRecommendationItemInput {
  label?: string;
  baseText?: string;
  devicesMode?: ApartmentRecommendationMode;
  devicePosition?: 'prefix' | 'suffix';
  allowOtherDevice?: boolean;
  roomsMode?: ApartmentRecommendationMode;
  /** null = cały słownik pomieszczeń */
  roomIds?: string[] | null;
  allowOtherRoom?: boolean;
}

/** Pozycja katalogu zaleceń lokali (GET/POST/PATCH /recommendations/apartment-items). */
export interface ApartmentRecommendationItemDetail {
  id: string;
  label: string;
  baseText: string;
  /** Poprzednie treści — rozpoznają stare zapisy, nie trafiają do nowych. */
  legacyTexts: string[];
  devicesMode: ApartmentRecommendationMode;
  devicePosition: 'prefix' | 'suffix';
  allowOtherDevice: boolean;
  roomsMode: ApartmentRecommendationMode;
  roomIds: string[] | null;
  allowOtherRoom: boolean;
  /** 'airflow' = „Szczelne Mieszkanie" (podformularz dopływu w kodzie) */
  builtinKind: string | null;
  active: boolean;
  sortOrder: number;
}

/** Pozycja listy „Typ urządzenia" (GET/POST/PATCH /recommendations/device-options). */
export interface DeviceOptionDetail {
  id: string;
  label: string;
  /** Skrót do treści zalecenia; null ⇒ do treści idzie label. */
  protocolText: string | null;
  active: boolean;
  sortOrder: number;
}

// --- H1: widok „Wszystkie terminy" (kalendarz terminów wszystkich budynków) --------------
// Jeden wiersz = JEDEN termin jednego przeglądu. Numer terminu (I/II/III…) nadaje BACKEND
// (utils/inspectionTerms — numeracja slotowa); front tylko renderuje `termNumber`.
export interface TermRow {
  buildingId: string;
  address: string;
  clientName: string | null;
  inspectionId: string;
  termNumber: number;
  termDate: string;            // 'YYYY-MM-DD'
  lifecycleState: LifecycleState;
  ceebStatus: string | null;
  ceebCaseNumber: string | null;
  ceebAcceptedAt: string | null;
}

export interface TermsResponse {
  data: TermRow[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface TermsParams {
  dateFrom?: string;
  dateTo?: string;
  clientId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * PROMPT 5 (kontrakt-API): grafik do Excela — POST /api/terms/export.xlsx. Co najmniej jedno
 * kryterium (zaznaczone budynki, zakres dat, zarządca); backend odsyła plik + `X-Skipped-Count`
 * (zaznaczone budynki bez terminu w pliku) + nazwę w Content-Disposition (`grafik_<od>_<do>.xlsx`).
 */
export interface TermsExportParams {
  buildingIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  clientId?: string;
}

// POST /api/terms/export-mail (A8, lista Bronka 2026-10): ten sam plik co exportXlsx, wysłany e-mailem.
// `to` = jeden lub kilka adresów (`,`/`;`, maks. 5); pusty temat/treść = domyślne z serwera.
// Błędy `{ error, code }`: 503 MAIL_DISABLED (flaga MAIL_ENABLED wyłączona), 422 NO_TERMS, 502 MAIL_FAILED.
export interface ScheduleMailParams extends TermsExportParams {
  to: string;
  subject?: string;
  message?: string;
}

export interface ScheduleMailResult {
  sent: true;
  to: string[];
  subject: string;
  filename: string;
  buildings: number;
  skippedCount: number;
}

/**
 * A11 (uwagi Bronka, raport 2026-10-19; kontrakt-API) — import harmonogramu z Excela z panelu:
 * `POST /terms/import-xlsx` (multipart: `file` + `mode` preview|commit, `city`, `addApartments` 0|1,
 * `buildingIds` JSON — tylko commit: budynki z `writes:true` w podglądzie). Tylko admin/superadmin.
 * `action`: plan = zaplanuje, already = bez zmian (te same terminy), conflict = otwarty przegląd z innymi
 * terminami (nie nadpisujemy), skip = pominięty z powodem (`reason`). `note` = tekst z kolumny terminu zamiast
 * daty („Czekamy na remont dachu"). `apartmentsAdd` = numery lokali do dodania (budynek bez lokali, opcja).
 */
export type ScheduleImportAction = 'plan' | 'already' | 'conflict' | 'skip';

export interface ScheduleImportItem {
  rowNumber: number;
  excelAddress: string;
  building: { id: string; label: string; clientName: string | null } | null;
  matchHow: string | null;
  state: string | null;
  openInspection: { status: string; firstTermDate: string | null; secondTermDate: string | null } | null;
  firstTermDate: string | null;
  secondTermDate: string | null;
  apartmentCountExcel: number | null;
  apartmentsDb: number | null;
  apartmentsAdd: string[];
  apartmentsNote: string | null;
  action: ScheduleImportAction;
  reason: string | null;
  note: string | null;
  writes: boolean;
  result: { inspectionId: string | null; lifecycleState: string | null; error: string | null } | null;
}

export interface ScheduleImportResponse {
  mode: 'preview' | 'commit';
  file: { name: string; sheet: string; headerRow: number };
  city: string;
  addApartments: boolean;
  items: ScheduleImportItem[];
  summary: {
    rows: number; plan: number; already: number; conflict: number; skip: number;
    writes: number; apartmentsToAdd: number; buildingsWithApartments: number;
  };
  result?: { planned: number; apartmentsAdded: number; errors: number };
}

export interface ScheduleImportParams {
  file: File;
  mode: 'preview' | 'commit';
  city?: string;
  addApartments?: boolean;
  buildingIds?: string[];
}

export const termsApi = {
  sendMail: async (params: ScheduleMailParams): Promise<ScheduleMailResult> => {
    const response = await authedFetch(`${API_BASE_URL}/terms/export-mail`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      // Komunikat z serwera jest dla użytkownika (np. „Wysyłka e-mail wyłączona na serwerze…").
      throw new Error((err as any).error || `Nie udało się wysłać grafiku (HTTP ${response.status})`);
    }
    return response.json();
  },

  importXlsx: async (params: ScheduleImportParams): Promise<ScheduleImportResponse> => {
    const fd = new FormData();
    fd.append('file', params.file, params.file.name);
    fd.append('mode', params.mode);
    if (params.city) fd.append('city', params.city);
    fd.append('addApartments', params.addApartments ? '1' : '0');
    if (params.buildingIds) fd.append('buildingIds', JSON.stringify(params.buildingIds));
    // Bez Content-Type — przeglądarka sama dopisze multipart z granicą (boundary).
    const response = await authedFetch(`${API_BASE_URL}/terms/import-xlsx`, { method: 'POST', body: fd });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się wczytać harmonogramu (HTTP ${response.status})`);
    }
    return response.json();
  },

  list: async (params: TermsParams = {}): Promise<TermsResponse> => {
    const qs = new URLSearchParams();
    if (params.dateFrom) qs.append('dateFrom', params.dateFrom);
    if (params.dateTo) qs.append('dateTo', params.dateTo);
    if (params.clientId) qs.append('clientId', params.clientId);
    if (params.page != null) qs.append('page', String(params.page));
    if (params.pageSize != null) qs.append('pageSize', String(params.pageSize));
    const q = qs.toString();
    const response = await authedFetch(`${API_BASE_URL}/terms${q ? `?${q}` : ''}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się pobrać terminów (HTTP ${response.status})`);
    }
    return response.json();
  },

  exportXlsx: async (
    params: TermsExportParams,
  ): Promise<{ blob: Blob; skipped: number; filename: string }> => {
    const response = await authedFetch(`${API_BASE_URL}/terms/export.xlsx`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się pobrać grafiku (HTTP ${response.status})`);
    }
    const { blob, filename } = await toDownloadedFile(response);
    return {
      blob,
      skipped: Number(response.headers.get('X-Skipped-Count') || 0),
      filename: filename || 'grafik.xlsx',
    };
  },
};

// --- Akceptacja protokołu w CEEB (036; zielony przycisk — TYLKO superadmin) --------------
// Bot: prawy klik na wierszu → „Akceptuj" → OK. NIEODWRACALNE → wymaga confirm:true.
/**
 * Status zadania akceptacji. `mail` (H4, #189) jest ADDYTYWNE: starszy backend go nie
 * przysyła, a `{ skipped: 'disabled' }` to normalny stan przy wyłączonej fladze MAIL_ENABLED.
 */
export interface CeebAcceptStatus {
  status: string;
  jobId?: string;
  error?: string;
  acceptedAt?: string;
  mail?: { sent?: boolean; messageId?: string | null; skipped?: string; error?: string };
}

/**
 * Bramka akceptacji na sucho — `GET /buildings/:id/ceeb-accept-check` (2026-10-09, incydent Kawika).
 * `blocked` = powód, dla którego serwer odmówi; `requiresAcknowledge` = jedyna blokada, którą superadmin
 * może świadomie obejść (lokale z terenu poza CEEB, poprawione ręcznie w CEEB).
 */
export interface CeebAcceptCheck {
  buildingId: string;
  address: string;
  caseNumber: string | null;
  ceebStatus: string | null;
  acceptedAt: string | null;
  aptTerminal: number;
  aptOpen: number;
  aptPendingCeeb: number;
  pendingApartments: string[];
  requiresAcknowledge: boolean;
  blocked: { code: string; error: string } | null;
  busy: { code: string; error: string } | null;
}

/** Wynik jednego budynku w partii akceptacji (`pominiety` = bramka/blokada, bot nie ruszył). */
export interface CeebAcceptBatchWynik {
  buildingId: string;
  adres: string;
  status: 'ok' | 'pominiety' | 'blad';
  caseNumber?: string;
  code?: string | null;
  error?: string | null;
}

/** Status partii — `GET /buildings/ceeb-accept-batch/:jobId`. */
export interface CeebAcceptBatchStatus {
  status: 'idle' | 'running' | 'done' | 'error';
  jobId?: string;
  total?: number;
  done?: number;
  current?: { buildingId: string; adres: string } | null;
  wyniki?: CeebAcceptBatchWynik[];
  error?: string;
}

const acceptJson = async <T,>(response: Response, fallback: string): Promise<T> => {
  if (!response.ok) {
    const err = await response.json().catch(() => ({} as any));
    throw new Error((err as any).error || `${fallback} (HTTP ${response.status})`);
  }
  return response.json();
};

export const ceebAcceptApi = {
  check: async (buildingId: string): Promise<CeebAcceptCheck> => acceptJson(
    await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-accept-check`, { cache: 'no-store' }),
    'Nie udało się sprawdzić protokołu',
  ),
  /** `acknowledgePending` — superadmin potwierdza, że lokale spoza CEEB poprawiono w CEEB ręcznie. */
  accept: async (buildingId: string, opts: { acknowledgePending?: boolean } = {}): Promise<{ jobId: string; caseNumber: string }> => acceptJson(
    await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: true, ...(opts.acknowledgePending ? { acknowledgePending: true } : {}) }),
    }),
    'Nie udało się zaakceptować',
  ),
  status: async (buildingId: string): Promise<CeebAcceptStatus> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-accept-status`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  },
  // Akceptacja ZBIORCZA — sekwencyjnie po stronie serwera; budynki za bramką są pomijane (bez obejścia).
  startBatch: async (buildingIds: string[]): Promise<{ jobId: string; total: number }> => acceptJson(
    await authedFetch(`${API_BASE_URL}/buildings/ceeb-accept-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ buildingIds, confirm: true }),
    }),
    'Nie udało się uruchomić akceptacji zbiorczej',
  ),
  batchStatus: async (jobId: string): Promise<CeebAcceptBatchStatus> => acceptJson(
    await authedFetch(`${API_BASE_URL}/buildings/ceeb-accept-batch/${jobId}`, { cache: 'no-store' }),
    'Nie udało się odczytać postępu',
  ),
};

// --- Tablety: stan zdrowia urządzeń (2026-10-09, migr. 061; kontrakt-API: NOWY endpoint) --------
// GET /api/devices (admin/superadmin, company scope). Tablet raportuje po każdym przebiegu
// synchronizacji (POST /api/devices/report); flagi liczy backend (utils/deviceHealth.js).
export interface DeviceFlags {
  /** Wersja niższa niż aktualna (albo nieczytelna). */
  outdated: boolean;
  /** Wersja niższa niż MIN_CLIENT_VERSION (albo nieznana) przy włączonej bramce — zapisy dostają 426. */
  belowMin: boolean;
  /** Rekordy odrzucone przez serwer (failed > 0, w tym konflikty) — same nie zejdą. */
  stuck: boolean;
  /** Są rekordy do wysłania, najstarszy czeka > 2 h. */
  staleQueue: boolean;
  /** Brak raportu od 3 dni przy niewysłanych danych. */
  silent: boolean;
}

export interface DeviceHealth {
  deviceId: string;
  shortId: string;
  /**
   * „Stara aplikacja" (≤ deeb-pwa-v33, bez raportów stanu) na koncie `userEmail` w przeglądarce `userAgent` — NIE
   * urządzenie: odnotowana przy zapisie z originu PWA bez X-App-Version; `lastSeenAt` = ostatni taki zapis.
   * Wersja i liczniki NIEZNANE (w odpowiedzi null/zera). Po 7 dniach bez zapisu: zero flag, `problem: false`.
   */
  legacy: boolean;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  appVersion: string | null;
  pending: number;
  failed: number;
  conflicts: number;
  blocked: number;
  oldestPendingAt: string | null;
  oldestFailedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  userAgent: string | null;
  online: boolean | null;
  firstSeenAt: string;
  lastSeenAt: string;
  flags: DeviceFlags;
  problem: boolean;
}

export interface DevicesResponse {
  /** Aktualna wersja PWA (max z env CURRENT_PWA_VERSION i zgłoszonych w 14 dni); null = nieznana. */
  currentVersion: string | null;
  /** Bramka zapisu MIN_CLIENT_VERSION; null = wyłączona. */
  minVersion: string | null;
  /** Problemy najpierw, potem ostatnio widziane. */
  devices: DeviceHealth[];
}

/** E2 Z4: lokal trzymany niewysłany na tablecie (GET /api/devices/held?buildingId=) — bez treści rekordu. */
export interface TabletHeldRecord {
  deviceId: string;
  shortId: string;
  appVersion: string | null;
  lastSeenAt: string | null;
  userEmail: string | null;
  inspectionId: string;
  apartmentId: string | null;
  apartmentNumber: string | null;
  updatedAt: string | null;
  state: 'pending' | 'failed' | 'conflict';
}

export const devicesApi = {
  held: async (buildingId: string): Promise<TabletHeldRecord[]> => {
    const response = await authedFetch(`${API_BASE_URL}/devices/held?buildingId=${encodeURIComponent(buildingId)}`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się pobrać stanu tabletów (HTTP ${response.status})`);
    }
    return ((await response.json()) as { held: TabletHeldRecord[] }).held;
  },
  list: async (): Promise<DevicesResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/devices`);
    if (!response.ok) {
      const err = await response.json().catch(() => ({} as any));
      throw new Error((err as any).error || `Nie udało się pobrać listy tabletów (HTTP ${response.status})`);
    }
    return response.json();
  },
};

// --- Poprawka WYSTAWIONEGO protokołu w CEEB („edit-issued", kontrakt-API 2026-10-09; TYLKO superadmin) ---
// Decyzja właściciela 2026-10-09: bot poprawia protokół „Wprowadzony" naszej firmy z bieżącego roku, zawsze po pełnej
// kopii z CEEB. Postęp: ISTNIEJĄCY ceebPushStatus (ten sam status-file i blokada, `mode: 'edit-issued'`).

/** Ostatni przebieg poprawki dla tego przeglądu (z audytu / status-file'u). */
export interface CeebEditIssuedLastRun {
  jobId: string;
  dryRun: boolean;
  status: string;
  finishedAt: string | null;
  snapshotBeforeId: string | null;
  snapshotAfterId: string | null;
}

/** GET /buildings/:id/ceeb-edit-issued-check — zawsze 200; `allowed:false` + `code` + gotowy polski `error`. */
export interface CeebEditIssuedCheck {
  allowed: boolean;
  /**
   * Kolejność sprawdzania: EDIT_ISSUED_DISABLED · FORBIDDEN_CLIENT · NO_CASE_NUMBER · PROTOCOL_ACCEPTED · NOT_ENTERED ·
   * PREVIOUS_YEAR · SINGLE_LOCAL_NOT_SUPPORTED · NOTHING_TO_CHANGE · EDIT_ISSUED_ALARM_HOLD (alarm poprzedniego przebiegu
   * czeka na człowieka) · IMPORT_RUNNING · PUSH_IN_PROGRESS · PUSH_BUSY · CEEB_BUSY. UI pokazuje `error`, kodu nie tłumaczy.
   */
  code: string | null;
  error: string | null;
  buildingId: string;
  inspectionId: string | null;
  address: string | null;
  caseNumber: string | null;
  ceebStatus: string | null;
  caseYear: number | null;
  acceptedAt: string | null;
  /** Lokale z danymi z terenu, które bot wpisze do protokołu (status = kanon AIR). */
  pending: Array<{ nr: string; status: string | null }>;
  /** Brak wcześniejszego przebiegu → UI domyślnie włącza „Najpierw próba bez zapisu". */
  firstRun?: boolean;
  /** Brak udanej próby bez zapisu dla tego przeglądu — serwer odmówi prawdziwej poprawki (409 DRY_RUN_REQUIRED). */
  dryRunRequired?: boolean;
  lastRun?: CeebEditIssuedLastRun | null;
  /** Gotowe polskie zdania „Czego nie ruszymy". */
  untouched?: string[];
}

export interface CeebEditIssuedStartParams {
  /** Numer sprawy przepisany przez operatora (serwer porównuje z numerem przeglądu → 409 CASE_CONFIRM_MISMATCH). */
  caseNumber: string;
  dryRun: boolean;
  /** Podzbiór numerów z `pending`; pominięte = wszystkie. */
  onlyLokale?: string[];
}

/** 202 z POST /buildings/:id/ceeb-edit-issued. */
export interface CeebEditIssuedStartResponse {
  status: string;
  jobId: string;
  buildingId: string;
  inspectionId: string | null;
  mode: string;
  dryRun: boolean;
  apartmentCount: number;
  caseNumber: string;
}

/** Kopia protokołu z CEEB (przed/po poprawce) — GET /buildings/:id/ceeb-snapshots, najnowsze pierwsze. */
export interface CeebSnapshot {
  id: string;
  caseNumber: string;
  phase: string;
  protocolStatus: string | null;
  localsCount: number | null;
  complete: boolean;
  takenAt: string;
  takenBy: string | null;
  jobId: string | null;
  hasPdf: boolean;
  pdfSha256: string | null;
}

/**
 * GET /ceeb/flags (TYLKO superadmin, 403 dla innych) — przełączniki nowych ścieżek ZAPISU do CEEB, domyślnie WYŁĄCZONE.
 * Panel pokazuje akcję poprawki wyłącznie przy `CEEB_EDIT_ISSUED === true` — wdrożenie kodu nie zmienia zachowania.
 */
export interface CeebFlags {
  CEEB_EDIT_ISSUED: boolean;
  CEEB_ACCEPT_SETS_END_DATE: boolean;
  // Serwer zwraca wszystkie flagi z utils/ceebFlags.js (FLAGS) — starszy backend mógł nie znać dwóch ostatnich.
  CEEB_UPDATE_ISSUED_COPIES?: boolean;
  CEEB_SINGLE_LOCAL_ENABLED?: boolean;
  // 079: własny załącznik budynku (karty mieszkań, PDF z panelu) zamiast naszych kart lokali.
  CEEB_OWN_CARDS_ATTACHMENT?: boolean;
}

export const ceebFlagsApi = {
  get: async (): Promise<CeebFlags> => {
    const response = await authedFetch(`${API_BASE_URL}/ceeb/flags`, { cache: 'no-store' });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się odczytać przełączników CEEB (HTTP ${response.status})`);
    return response.json();
  },
};

// Pole `X-Client: web-admin` (wymagane przez serwer — inaczej 403 FORBIDDEN_CLIENT) dokłada authedFetch.
export const ceebEditIssuedApi = {
  check: async (buildingId: string): Promise<CeebEditIssuedCheck> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-edit-issued-check`, { cache: 'no-store' });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się sprawdzić protokołu (HTTP ${response.status})`);
    return response.json();
  },
  // `confirm: true` zawsze — serwer bez niego odpowiada 400 CONFIRM_REQUIRED (zmiana dokumentu w rejestrze państwowym).
  start: async (buildingId: string, params: CeebEditIssuedStartParams): Promise<CeebEditIssuedStartResponse> => {
    const body: Record<string, unknown> = { confirm: true, caseNumber: params.caseNumber, dryRun: params.dryRun };
    if (params.onlyLokale) body.onlyLokale = params.onlyLokale;
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-edit-issued`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się uruchomić poprawki protokołu (HTTP ${response.status})`);
    return response.json();
  },
  snapshots: async (buildingId: string): Promise<{ snapshots: CeebSnapshot[] }> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-snapshots`, { cache: 'no-store' });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się pobrać kopii protokołu (HTTP ${response.status})`);
    return response.json();
  },
  // PDF kopii przez authedFetch (JWT w nagłówku — goły link <a href> nie przeszedłby AUTH_ENFORCE). 404 = kopia bez PDF-a.
  snapshotPdf: async (buildingId: string, snapshotId: string): Promise<DownloadedFile> => {
    const response = await authedFetch(
      `${API_BASE_URL}/buildings/${buildingId}/ceeb-snapshots/${encodeURIComponent(snapshotId)}?format=pdf`,
    );
    if (!response.ok) throw await ceebApiError(response, `Nie udało się pobrać PDF-u kopii (HTTP ${response.status})`);
    return toDownloadedFile(response);
  },
};

// --- Odpięcie numeru sprawy protokołu usuniętego w CEEB (RAPORT G8, superadmin) ---------------------------------
// Serwer sam ustala, czy wolno (ostatnia operacja bota = odmowa „Nie znaleziono protokołu…" dla zapisanego numeru) —
// panel tylko pokazuje akcję, gdy `available`, i przepisuje numer z odpowiedzi do POST.
export interface CeebUnlinkCaseCheck {
  buildingId: string;
  inspectionId: string | null;
  caseNumber: string | null;
  available: boolean;
  /** Kody: NO_INSPECTION · NO_CASE_NUMBER · PROTOCOL_ACCEPTED · PUSH_IN_PROGRESS · ACCEPT_IN_PROGRESS · PHASE_A_IN_PROGRESS ·
   *  NO_TARGET_NOT_FOUND · FAILURE_FOR_OTHER_CASE · CASE_SEEN_AFTER_FAILURE · CASE_SEEN_AFTER_MISSING · BOT_RUN_AFTER_MISSING.
   *  UI pokazuje `error`, kodu nie tłumaczy. */
  blocked: { code: string; error: string } | null;
  failure: { auditId: string; action: string; at: string; jobId: string | null; refusalCode: string | null; caseNumber: string | null } | null;
  /** 074 (addytywne): dowód odpięcia — odmowa bota albo pełny nocny skan (protokołu nie ma na liście CEEB). */
  evidence?: 'bot-refusal' | 'missing-in-scan' | null;
  /** 074 (addytywne): kiedy nocny skan wykrył brak protokołu w CEEB; null = nie wykrył. */
  deletedInCeebAt?: string | null;
}

export interface CeebUnlinkCaseResponse {
  ok: true;
  inspectionId: string;
  caseNumber: string;
  cleared: string[];
  /** 074 (addytywne): ile zapisów lokali zaimportowanych z tego protokołu przestało liczyć się jako „w CEEB”. */
  airUnsynced?: number;
}

export const ceebUnlinkCaseApi = {
  check: async (buildingId: string): Promise<CeebUnlinkCaseCheck> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-unlink-case-check`, { cache: 'no-store' });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się sprawdzić numeru sprawy (HTTP ${response.status})`);
    return response.json();
  },
  unlink: async (buildingId: string, caseNumber: string): Promise<CeebUnlinkCaseResponse> => {
    const response = await authedFetch(`${API_BASE_URL}/buildings/${buildingId}/ceeb-unlink-case`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ caseNumber }),
    });
    if (!response.ok) throw await ceebApiError(response, `Nie udało się odpiąć numeru sprawy (HTTP ${response.status})`);
    return response.json();
  },
};
